/**
 * @typedef IPriorWebRootScope
 * @prop {string} appName - App name as set in config.js
 * @prop {ng.IPromise<void>} loadedCategories - Promise that resolves when categories have been loaded from API
 * @prop {ng.IPromise<any>} loadedEverything - Promise that resolves when all information hashprefix been loaded from API
 * @prop {ng.IPromise<void>} loadedItems - Promise that resolves when menu items have been loaded from API
 * @prop {ng.IPromise<void>} loadedRestaurants - Promise that resolves when restaurants have been loaded from API
 */

/** @type {object} */
var mangoPay;
(function(angular, mangoPay) {
  var app = angular.module('priorWeb', [
    'angularMoment', 'dcbImgFallback', 'ezfb', 'ngResource', 'ngRoute', 'ngStorage', 'pascalprecht.translate',
    'priorCore', 'priorConfig', 'ui.bootstrap',
  ]);

  app.config([
    '$compileProvider', '$locationProvider', '$routeProvider', '$translateProvider', 'ezfbProvider', 'apiEndpoint',
    'defaultLanguage', 'defaultLanguageTranslations', 'facebookAppId', 'isDev', 'gaUseGtag', 'GoogleAnalyticsProvider',
    /**
     * @param {ng.ICompileProvider} $compileProvider
     * @param {ng.ILocationProvider} $locationProvider
     * @param {ng.route.IRouteProvider} $routeProvider
     * @param {ng.translate.ITranslateProvider} $translateProvider
     * @param {string} apiEndpoint
     * @param {string} defaultLanguage
     * @param {ng.translate.ITranslationTable} defaultLanguageTranslations
     * @param {string} facebookAppId
     * @param {boolean} isDev
     * @param {boolean} gaUseGtag
     * @param {GoogleAnalyticsProvider} googleAnalyticsProvider
     */
    function (
      $compileProvider, $locationProvider, $routeProvider, $translateProvider, ezfbProvider, apiEndpoint,
      defaultLanguage, defaultLanguageTranslations, facebookAppId, isDev, gaUseGtag, googleAnalyticsProvider
    ) {
      $compileProvider.commentDirectivesEnabled(false);
      $compileProvider.cssClassDirectivesEnabled(false);

      $compileProvider.debugInfoEnabled(isDev);

      $locationProvider.hashPrefix('');

      $routeProvider
        .when('/', {
          templateUrl: 'templates/restaurants.html',
          controller: 'RestaurantsController'
        })
        .when('/menu/:restaurantId/:menuItemId?', {
          templateUrl: 'templates/menu.html',
          controller: 'MenuController'
        })
        .when('/menu/:restaurantId/token/:resetPasswordToken', {
          templateUrl: 'templates/menu.html',
          controller: 'MenuController'
        })
        .when('/payment', {
          templateUrl: 'templates/payment.html',
          controller: 'PaymentController'
        })
        .when('/ordered/:restaurantId/:orderId', {
          templateUrl: 'templates/ordered.html',
          controller: 'OrderedController'
        });

      ezfbProvider.setInitParams({
        appId: facebookAppId,
        version: 'v6.0'
      });
      ezfbProvider.setLocale(defaultLanguage);

      $translateProvider.usePostCompiling(true)
        .addInterpolation('$translateMessageFormatInterpolation')
        .fallbackLanguage(defaultLanguage)
        .preferredLanguage(defaultLanguage)
        .translations(defaultLanguage, defaultLanguageTranslations)
        .uniformLanguageTag('bcp47')
        .useSanitizeValueStrategy(null)
        .useUrlLoader(apiEndpoint + '/translations', {
          queryParameter: 'l'
        })
        .forceAsyncReload(true)
        .use(defaultLanguage);

      if (isDev) {
        $translateProvider.useMissingTranslationHandlerLog();
      }

      googleAnalyticsProvider.setUseGtag(gaUseGtag);
    }
  ]);

  app.run([
    '$anchorScroll', '$localStorage', '$q', '$rootScope', 'ActionEnum', 'CategoriesRepository', 'Category',
    'CurrentOrder', 'CurrentUser', 'CustomizedCompositeMenuItemModel', 'FacebookLogin', 'FbPixel', 'Localization',
    'MenuItem', 'MenuItemsRepository', 'Order', 'OrderItemMapper', 'OrderStatusEnum', 'Restaurant',
    'RestaurantsRepository', 'State', 'SpecificMenuItemModel', 'Timezone', 'amMoment', 'appName', 'brandId',
    'defaultLanguage', 'facebookPixelId', 'GoogleAnalytics', 'mangopayBaseUrl', 'mangopayClientId', 'moment',
    /**
     * @param {ng.IAnchorScrollService} $anchorScroll
     * @param {import("ngStorage").ngStorage.StorageService} $localStorage
     * @param {ng.IQService} $q
     * @param {IPriorWebRootScope & ng.IRootScopeService} $rootScope
     * @param {string} appName
     * @param {GoogleAnalyticsWrapper} googleAnalytics
     * @param {string} facebookPixelId
     * @param {string} mangopayBaseUrl
     * @param {string} mangopayClientId
     */
    function(
      $anchorScroll, $localStorage, $q, $rootScope, ActionEnum, CategoriesRepository, Category, CurrentOrder,
      CurrentUser, CustomizedCompositeMenuItemModel, FacebookLogin, FbPixel, Localization, MenuItem,
      MenuItemsRepository, Order, OrderItemMapper, OrderStatusEnum, Restaurant, RestaurantsRepository, State,
      SpecificMenuItemModel, Timezone, amMoment, appName, brandId, defaultLanguage, facebookPixelId, googleAnalytics,
      mangopayBaseUrl, mangopayClientId, moment
    ) {
      $rootScope.appName = appName;

      $rootScope.$on('$routeChangeStart', function(__event__, next, current) {
        if (!!next && !!current) {
          $anchorScroll('header');
        }
      });

      Localization.setLocale(defaultLanguage);
      amMoment.changeLocale(defaultLanguage);

      FacebookLogin.getLoginStatus();

      FbPixel.init(facebookPixelId);

      // Configure MangoPay card registration kit
      mangoPay.cardRegistration.baseURL = mangopayBaseUrl;
      mangoPay.cardRegistration.clientId = mangopayClientId;

      var timezone = moment.tz(moment.tz.guess());
      Timezone.offset = timezone.utcOffset() * 60;
      Timezone.dst = timezone.isDST();

      /** @type {ng.IDeferred<void>} */
      var restDefer = $q.defer();
      /** @type {ng.IDeferred<void>} */
      var categDefer = $q.defer();
      /** @type {ng.IDeferred<void>} */
      var itemsDefer = $q.defer();
      /** @type {ng.IDeferred<void>} */
      var currentOrderDefer = $q.defer();

      $rootScope.loadedRestaurants = restDefer.promise;
      $rootScope.loadedCategories = categDefer.promise;
      $rootScope.loadedItems = itemsDefer.promise;
      $rootScope.loadedEverything = $q.all(
        [$rootScope.loadedRestaurants, $rootScope.loadedCategories, $rootScope.loadedItems, currentOrderDefer.promise]
      );

      Restaurant.query({ brand_id: brandId }).$promise.then(function(restaurants) {
        RestaurantsRepository.removeAll();
        RestaurantsRepository.saveAll(restaurants);

        restDefer.resolve();
      }).catch(function(err) {
        if (!err.status || err.status !== 304) {
          console.log(err);
        }
      });

      var currentOrderId = State.get().currentOrder.id,
          pendingPreAuthId = State.get().currentOrder.pendingPreAuthId,
          restaurantId = State.get().app.restaurantId;

      if (restaurantId) {
        restDefer.promise.then(function() {
          Category.query({
            restaurantId: restaurantId
          }).$promise
            .then(function(categories) {
              CategoriesRepository.saveAll(categories);

              categDefer.resolve();
            })
            .then(function(err) {
              if (err !== 304) {
                console.log(err);
              }
            });

          MenuItem.query({
            restaurantId: restaurantId
          }).$promise
            .then(function(menuItems) {
              MenuItemsRepository.saveAll(menuItems);

              itemsDefer.resolve();
            })
            .catch(function(err) {
              if (!err.status || err.status !== 304) {
                console.log(err);
              }
            });

          var updateCurrentOrderData = currentOrderId && !pendingPreAuthId ?
              Order.get({ id: currentOrderId }).$promise :
              $q.resolve(false);

          itemsDefer.promise.then(function() {
            var state = State.get();
            state.items.customized.forEach(function(json) {
              var data = JSON.parse(json),
                  item = new CustomizedCompositeMenuItemModel(data);
              if (state.currentOrder.items.filter(function(orderItem) {
                return orderItem.menuItemId === item.getId();
              }).length > 0) {
                MenuItemsRepository.save(item);
              } else {
                State.dispatch({
                  type: ActionEnum.REMOVE_CUSTOMIZED_ITEM,
                  itemId: item.getId()
                });
              }
            });

            state.items.specific.forEach(function(json) {
              var data = JSON.parse(json),
                  item = new SpecificMenuItemModel(data);
              if (state.currentOrder.items.filter(function(orderItem) {
                return orderItem.menuItemId === item.getId();
              }).length > 0) {
                MenuItemsRepository.save(item);
              } else {
                State.dispatch({
                  type: ActionEnum.REMOVE_SPECIFIC_ITEM,
                  itemId: item.getId()
                });
              }
            });

            var restaurant = RestaurantsRepository.get(restaurantId);
            CurrentOrder.init(restaurant);

            updateCurrentOrderData.then(function(order) {
              if (!order ||
                  (order.getStatus() != OrderStatusEnum.ACCEPTED &&
                   order.getStatus() != OrderStatusEnum.REJECTED)) {
                state.currentOrder.items.forEach(function(item) {
                  var menuItem = MenuItemsRepository.get(item.menuItemId);
                  CurrentOrder.save(OrderItemMapper.map(menuItem));
                  for (var i = 1; i < item.quantity; i++) {
                    CurrentOrder.increaseItemQuantity(item.menuItemId);
                  }
                });

                CurrentOrder.setId(currentOrderId);
                CurrentOrder.setComments(state.currentOrder.comments);
                CurrentOrder.setDeliveryAddress(state.currentOrder.deliveryAddress);
                CurrentOrder.setPaymentType(state.currentOrder.paymentType);
                CurrentOrder.setPhone(state.currentOrder.phone);
                CurrentOrder.setPromoCode(state.currentOrder.promoCode);
                CurrentOrder.setStatus(state.currentOrder.status);
                CurrentOrder.setWhen(state.currentOrder.when);
                CurrentOrder.setWhere(state.currentOrder.where);
                CurrentOrder.setZipCode(state.currentOrder.zipCode);

                if (pendingPreAuthId) {
                  State.dispatch({
                    type: ActionEnum.REQUEST_3DS_AUTHORIZATION,
                    preAuthId: pendingPreAuthId
                  });
                }
              }
            });
          });
        });
      } else {
        categDefer.resolve();
        itemsDefer.resolve();
      }

      $localStorage.state = State.get();
      currentOrderDefer.resolve();

      if (CurrentUser.isSet()) {
        googleAnalytics.setUserId(CurrentUser.get().getId());
      }

      State.subscribe(function() {
        var state = State.get()
        $localStorage.state = state;
      });
    }
  ]);
})(angular, mangoPay);

(function() {
  var config = angular.module('priorConfig', []);

  config.constant('apiEndpoint', 'https://sitio-api.priorhq.com');
  config.constant('appName', 'El Sitio');
  config.constant('brandId', 1);
  config.constant('isDev', false);
  config.constant('apiVersion', 2);
  config.constant('facebookAppId', '655384642475475');
  config.constant('facebookPixelId', '');
  config.constant('gtagPropertyIds', ['UA-55500817-93']);
  config.constant('gtmContainerIds', []);
  config.constant('trackGtagConversions', []);
  config.constant('trackGATransactions', ['UA-55500817-93']);
  config.constant('trackGAViews', ['UA-55500817-93']);
  config.constant('currencyMultiplier', 100);
  config.constant('currencyFractionDigits', 2);
  config.constant('kushkiClientId', 'de015dde66054e41a5f66e519f4e2588');
  config.constant('kushkiTestEnvironment', false);
  config.constant('mangopayClientId', 'prior');
  config.constant('mangopayBaseUrl', 'https://api.mangopay.com');
  config.constant('defaultLanguage', 'es-ES');
  config.constant('gaUseGtag', true);
})();

(function() {
  var config = angular.module('priorConfig');

  config.constant('defaultLanguageTranslations', {
    'ALERT_MODAL': {
      'ERROR_TITLE': '¡Uy, perdona!',
      'OK_BUTTON_LABEL': 'Vale',
      'SUCCESS_TITLE': ''
    },
    'CARD_MODAL': {
      'CCV_PLACEHOLDER': 'CCV (3 o 4 dígitos)',
      'EXPIRATION_DATE_PLACEHOLDER': 'Fecha de caducidad (MM/AA)',
      'MESSAGE': 'Introduce los datos de tu tarjeta de pago',
      'NAME_PLACEHOLDER': 'Nombre',
      'NUMBER_PLACEHOLDER': 'Número de la tarjeta',
      'TITLE': 'Datos de pago'
    },
    'COMPOSITE_MENU_ITEM_MODAL': {
      'COMPOSITE_RULE_ERROR_MESSAGE': '{exactRule, select, false{de {minCount} a {maxCount} cosas de {sectionName}} other{{minCount, plural, one{# cosa} other{# cosas}} de {sectionName}}}',
      'OR_LABEL': 'o',
      'PICK_LABEL': 'Elige'
    },
    'EDITABLE_COMPOSITE_MENU_ITEM_MODAL': {
      'INVALID_SELECTION_MODAL_TITLE': '¡Uy, algo falla!',
      'INVALID_SELECTION_MODAL_BUTTON': '@:CARD_PAGE.ERROR_MODAL_BUTTON_LABEL',
      'SAVE_BUTTON': 'Guardar',
      'TITLE': 'Configura'
    },
    'EDITABLE_COMPOSITE_MENU_ITEM_MODEL': {
      'INVALID_SELECTION_MESSAGE': '{emptyPrev, select, false{{prev}<br /> o <br />{current}} other{{current}}}'
    },
    'GENERIC_MENU_ITEM_MODAL': {
      'OPTIONS_LABEL': 'Opciones',
      'PICK_AN_OPTION_MESSAGE': 'Elige una opción',
      'SAVE_BUTTON': '@:EDITABLE_COMPOSITE_MENU_ITEM_MODAL.SAVE_BUTTON',
      'TITLE': '@:EDITABLE_COMPOSITE_MENU_ITEM_MODAL.TITLE'
    },
    'LOGIN_MODAL': {
      'ACCEPT_TOS_MESSAGE': 'Al registrate aceptas nuestras condiciones de uso',
      'EMAIL_PLACEHOLDER': 'Email',
      'EMAIL_SUGGESTION': '¿Quisiste decir ',
      'FACEBOOK_LOGIN_ERROR_MODAL_MISSING_EMAIL_PERMISSION_MESSAGE': 'We need your email address to identify your account. Please allow access to your email address from Facebook or, if you prefer it, create an account with a different email.',
      'GO_LOGIN_BUTTON': 'Entrar a mi cuenta',
      'GO_REGISTER_BUTTON': 'Crear nueva cuenta',
      'LOGIN_BUTTON': 'Entrar',
      'LOGIN_OR_REGISTER_MESSAGE': 'Entra a tu cuenta o crea una nueva.',
      'LOGIN_WITH_FACEBOOK_LABEL': 'Entrar con Facebook',
      'OR_LOGIN_WITH_EMAIL_LABEL': 'o si prefieres con tu email y contraseña',
      'OR_REGISTER_WITH_EMAIL_LABEL': 'o si prefieres con tu email y contraseña',
      'PASSWORD_PLACEHOLDER': 'Contraseña',
      'REGISTER_BUTTON': 'Crear cuenta',
      'REGISTER_WITH_FACEBOOK_LABEL': 'Crear cuenta con Facebook',
      'RESET_PASSWORD_BUTTON': 'Recuperar contraseña',
      'RESET_PASSWORD_LABEL': 'Recuperar contraseña',
      'RESET_PASSWORD_LINK_SENT': 'Te hemos enviado un enlace para recuperar tu contraseña al correo indicado!',
      'RESET_PASSWORD_MODAL_TITLE': 'Recuperar contraseña',
      'RETURN_TO_LOGIN_BUTTON': 'Volver',
      'TITLE': 'Entra o Registrate',
      'UNKNOWN_ERROR': 'Ha habido un error. Por favor intentalo de nuevo!',
      'USER_NOT_FOUND_ERROR': 'No se ha encontrado un usuario con ese correo!'
    },
    'MENU_PAGE': {
      'CATEGORIES_LABEL': 'Categorías',
      'KITCHEN_CLOSED_MESSAGE': 'Nuestras cocinas están cerradas. Puedes volver a intentarlo {openToday, select, false{mañana} other{a las {nextStartTime}}}.',
      'REVIEW_ORDER_BUTTON': 'Revisar pedido',
      'SEND_ORDER_BUTTON_END': 'el pago',
      'SEND_ORDER_BUTTON_START': 'En seguida puedes detallar y confirmar tu pedido antes de hacer'
    },
    'NAVBAR': {
      'RESTAURANT_CLOSED_LABEL': 'Cerrado'
    },
    'ORDERED_PAGE': {
      'BUSY_REJECT_REASON': 'Lo sentimos, pero estamos ocupados. Vuelve a intentarlo en unos minutos.',
      'GENERIC_REJECT_REASON': 'Lo sentimos pero no hemos podido realizar tu pedido. Vuelve a intentarlo en algunos minutos.',
      'GO_TO_HOME_PAGE_LABEL': 'Volver al inicio',
      'MISSING_ITEM_REJECT_REASON': 'Lo sentimos, pero no tenemos los ingredientes necesarios para tu pedido. ¿Quieres pedir otra cosa?',
      'ORDER_ACCEPTED_DELIVERY_MESSAGE': 'Nuestro repartidor te lo entregará a esa hora.',
      'ORDER_ACCEPTED_LABEL': 'Pedido confirmado',
      'ORDER_ACCEPTED_MESSAGE': 'Lo tendrás el <span>{{date}}</span> a las <span>{{time}}</span>',
      'ORDER_AGAIN_LABEL': 'Volver a pedir',
      'ORDER_LABEL': 'Pedido',
      'ORDER_NOT_CONFIRMED_EXTRA_MESSAGE': 'Tu pedido ya se está tramitando, si tienes alguna duda puedes llamarnos al {{phoneNumber}}',
      'ORDER_NOT_CONFIRMED_MESSAGE': 'Pedido enviado al restaurante. En breves momentos te confirmaremos la hora.',
      'ORDER_REJECTED_LABEL': 'Pedido denegado',
      'ORDER_SENT_LABEL': 'Pedido enviado'
    },
    'ORDER_REVIEW_MODAL': {
      'EDIT_ORDER': 'Editar pedido',
      'ORDER_TOTAL': 'Total: {{ total | priorCurrency }}',
      'PAYMENT_DETAILS_CASH': 'Pagarás tu pedido en efectivo',
      'PAYMENT_DETAILS_RAPPI_PAY': 'Pagarás tu pedido con RappiPay',
      'PAYMENT_DETAILS_REDIRECT': 'En el siguiente paso podrás hacer el pago de forma segura',
      'PAYMENT_DETAILS_TPV': 'Pagarás tu pedido con datáfono',
      'PHONE_NOT_GIVEN': 'No has rellenado un teléfono de contacto, que puede ser de ayuda en caso de que el restaurante necesite contactar contigo',
      'PLACE_ORDER': 'Enviar pedido',
      'TITLE': 'Revisa tu pedido'
    },
    'PAYMENT_PAGE': {
      '3DS_CREDIT_CARD_ERROR': 'Ha habido un problema al autorizar el pago con tu banco, por favor vuelve a intentarlo.',
      'ADD_CARD_BUTTON': 'Añadir tarjeta de pago',
      'ADD_COUPON_MESSAGE': 'Introduce tu cupón y el descuento se reflejará en el total de tu pedido.',
      'ADDRESS_HEADING': 'Dirección y contacto',
      'ADDRESS_PLACEHOLDER': 'Dirección',
      'APPLY_COUPON_BUTTON': 'Canjear cupón',
      'ASAP_OPTION_LABEL': 'Lo antes posible',
      'CARD_COULD_NOT_BE_DELETED_MESSAGE': 'Ha habido un error al borrar la tarjeta, por favor intenta de nuevo.',
      'CARD_DELETED_MESSAGE': 'La tarjeta ha sido borrada con éxito.',
      'CHANGE_COUPON_BUTTON': 'Cambiar',
      'COMMENTS_HEADING': 'Comentarios',
      'COMMENTS_PLACEHOLDER': 'Comentarios',
      'CORPORATE_DELIVERY_LABEL': 'Corporate delivery - Exclusivo empresas (indicar el cupon de la empresa)',
      'COUPON_HEADING': 'Cupón',
      'COUPON_PLACEHOLDER': 'Introduce el código',
      'CREDIT_DISCOUNT_LABEL': 'Saldo',
      'CREDIT_LEFT_MESSAGE': '(te {isPlural, select, true{quedaran} other{quedara}} {creditLeft})',
      'DELETE_CARD_BUTTON': 'Borrar',
      'DELIVERY_ADDRESS_INVALID': 'No podemos encontrar la dirección que has introducido. Por favor asegurate de introducir solo la calle, número y piso y recuerda que tienes el campo de comentarios para enviar cualquier otra información para el repartidor.',
      'DELIVERY_CHARGE_LABEL': 'Servicio de entrega',
      'DELIVERY_LABEL': 'Delivery',
      'DELIVERY_UNAVAILABLE_LABEL': 'Actualmente el servicio de entrega a domicilio no está disponible. Por favor, elige otra forma de obtener tu pedido o cambia la hora o la zona.',
      'DINING_IN_LABEL': 'Dining in',
      'DISCOUNT_LABEL': 'Descuento',
      'EDIT_CARD_BUTTON': 'Editar',
      'EDIT_ORDER_LABEL_START': 'Puedes',
      'EDIT_ORDER_BUTTON': 'editar tu pedido',
      'EDIT_ORDER_LINK': 'editar pedido',
      'EXPIRED_CREDIT_CARD_ERROR': 'La tarjeta que tienes guardada ha caducado. Por favor añade una nueva tarjeta y vuelve a intentar el pago.',
      'GENERIC_CREDIT_CARD_ERROR': 'No pudimos pagar tu pedido. ¿Podrías revisar los datos de tu tarjeta de pago y volver a intentarlo?',
      'GENERIC_SAVE_ORDER_ERROR': 'No pudimos enviar tu pedido. ¿Podrías volver a intentarlo?',
      'INACTIVE_CREDIT_CARD_ERROR': 'La tarjeta que tienes guardada está inactiva. Por favor añade una nueva tarjeta y vuelve a intentar el pago.',
      'INVALID_COUPON_ERROR': '¡El código es invalido! Comprueba que lo has introducido correctamente y vuelve a intentarlo, por favor.',
      'INVALID_NAME_MESSAGE': 'Introduce tu nombre y apellidos',
      'MINIMUM_ORDER_NOT_MET_MESSAGE': 'El pedido mínimo es de {{minimumOrder}}, sólo te faltan {{difference}} para alcanzarlo.',
      'MISSING_CARD_MESSAGE': '{hasCredit, select, 1{No tienes saldo suficiente para pagar el pedido. ¿Podrías añadir tu tarjeta para que te cobremos el importe que queda?} other{Tu pedido está casi listo, para pagarlo por adelantado necesitamos que añadas una tarjeta de pago.}}',
      'NAME_PLACEHOLDER': 'Nombre',
      'NOT_COVERED_DELIVERY_ADDRESS_ERROR_MODAL_MESSAGE': 'No entregamos en la dirección que has introducido. Por favor cambia de dirección o elige otro tipo de pedido (recoger o comer en el local).',
      'NO_CARD_ADDED_MESSAGE': 'Aún no añadiste una tarjeta de pago.',
      'OR_CONFIRM_AND_SEND_ORDER_LABEL': 'o confirmarlo para enviarlo a {{ appName }}.',
      'PAYMENT_HEADING': 'Pago',
      'PHONE_PLACEHOLDER': 'Teléfono',
      'PREAUTH_GENERIC_ERROR_MESSAGE': 'No pudimos pagar tu pedido. ¿Podrías revisar los datos de tu tarjeta de pago y volver a intentarlo?',
      'REDSYS_WRONG_CVV2_CVC2_ERROR_MESSAGE': 'El código CVV2/CVC2 que introdujiste no es correcto. ¿Podrías volver a intentarlo por favor?',
      'REDSYS_WRONG_EXPIRY_DATE_ERROR_MESSAGE': 'La fecha de caducidad de la tarjeta no está correcta. ¿Podrías volver a intentarlo por favor?',
      'RESTAURANT_CLOSED_LABEL': '@:PAYMENT_PAGE.RESTAURANT_CLOSED_SAVE_ORDER_ERROR',
      'RESTAURANT_CLOSED_SAVE_ORDER_ERROR': 'Nuestras cocinas están cerradas. Puedes volver a intentarlo {openToday, select, false{mañana} other{a las {nextStartTime}}}.',
      'SEND_ORDER_BUTTON': 'Realizar pedido',
      'TAKEOUT_LABEL': 'Lo recoges en el local',
      'TODAY_LABEL': 'Hoy',
      'TOMORROW_LABEL': 'Mañana',
      'TRANSACTION_DENIED_CREDIT_CARD_ERROR': 'Operación denegada. Por favor asegurate que tu tarjeta tiene saldo suficiente y vuelve a intentar el pago!',
      'UNVERIFIED_ACCOUNT_ERROR': 'Necesitamos verificar tu cuenta para que puedas hacer pedidos.',
      'WHAT_TIME_OPTION_LABEL': '¿A qué hora?',
      'WHERE_HEADING': 'Dónde',
      'YOUR_ORDER_LABEL': 'Tu pedido',
      'ZIP_CODE_PLACEHOLDER': 'Código postal'
    },
    'RESET_PASSWORD_MODAL': {
      'CLOSE_BUTTON_LABEL': 'Cerrar',
      'PASSWORD_CONFIRMATION_LABEL': 'Confirmación contraseña',
      'PASSWORD_LABEL': 'Nueva contraseña',
      'PASSWORD_MIN_LENGTH_HELP_MESSAGE': 'Mínimo 8 caracteres',
      'SAVE_BUTTON_LABEL': 'Guardar',
      'TITLE': 'Cambia tu contraseña',
      'USER_CANCELED_ERROR_MESSAGE': 'Has cancelado el proceso de cambio de contraseña.'
    }
  });
})();

(function() {
  angular
    .module('priorWeb')
    .component('paymentModule', {
      templateUrl: 'templates/payment-module.html',
      controller: 'PaymentModuleController',
      bindings: {
        cardNumber: '<',
        addCard: '&',
        deleteCard: '&',
        paymentTypeUpdated: '&'
      }
    });
})();

var mangoPay;
(function(mangoPay, Kushki) {
  var app = angular.module('priorWeb');

  app.controller('CardController', [
    '$scope', '$translate', 'Alert', 'Card', 'Currency', 'CurrentUser', 'FbPixelEventEnum', 'FbPixel',
    'GoogleAnalytics', 'MangopayCardRegistrationErrorEnum', 'PaymentProviderEnum', 'kushkiClientId',
    'kushkiTestEnvironment', 'trackGAViews',
    /**
     * @param {GoogleAnalyticsWrapper} googleAnalytics
     * @param {string[]} trackGAViews
     */
    function CardController(
      $scope, $translate, Alert, Card, Currency, CurrentUser, FbPixelEventEnum, FbPixel, googleAnalytics,
      MangopayCardRegistrationErrorEnum, PaymentProviderEnum, kushkiClientId, kushkiTestEnvironment, trackGAViews
    ) {
      $scope.formData = {
        name: CurrentUser.get().getName(),
        cardNumber: '',
        cardExpiration: '',
        cardCvx: ''
      };

      $scope.$on('$viewContentLoaded', function() {
        trackGAViews.forEach(function(propertyId) {
          googleAnalytics.trackView(propertyId, 'card');
        });
      });

      $scope.save = function() {
        $scope.formClass = 'ng-submitted';
        angular.element('.modal-footer button.btn-primary').addClass('disabled').prop('disabled', true);
        if ($scope.cardForm.$valid) {
          save();
        } else {
          angular.element('.modal-footer button.btn-primary').removeClass('disabled').prop('disabled', false);
        }
      };

      function save() {
        var saveCard = function(registeredCard) {
          Card.save(undefined, {
            provider_id: registeredCard.CardId,
            name: $scope.formData.name,
            number: registeredCard.CardBin || '',
            currency: Currency.get(),
            provider_type: $scope.paymentType
          }, function(savedCard) {
            var currentUser = CurrentUser.get();
            currentUser.setName($scope.formData.name),
            currentUser.setCard(savedCard);
            CurrentUser.set(currentUser);
            trackGAViews.forEach(function(propertyId) {
              googleAnalytics.trackEvent(propertyId, 'card', 'saved');
            });
            $scope.$close(savedCard);
            FbPixel.track(FbPixelEventEnum.ADD_PAYMENT_INFO);
          }, handleError);
        }

        if ($scope.paymentType === PaymentProviderEnum.KUSHKI) {
          var kushki = new Kushki({
            merchantId: kushkiClientId,
            inTestEnvironment: kushkiTestEnvironment
          });

          kushki.requestSubscriptionToken({
            card: {
              name: $scope.formData.name,
              number: $scope.formData.cardNumber,
              cvc: $scope.formData.cardCvx,
              expiryMonth: $scope.formData.cardExpiration.split('/')[0],
              expiryYear: $scope.formData.cardExpiration.split('/')[1]
            },
            currency: Currency.get()
          }, function(response) {
            if (response.error) {
              handleError(response);
            } else {
              saveCard({
                CardId: response.token,
                CardBin: $scope.formData.cardNumber.replace(/^([0-9]+)([0-9]{4})$/, 'XXXXXXXXXXXX$2')
              });
            }
          });
        } else if ($scope.paymentType === PaymentProviderEnum.MANGOPAY) {
          Card.createRegistrationData(undefined, {
            name: $scope.formData.name,
            currency: Currency.get()
          }, function(registrationData) {
            mangoPay.cardRegistration.init({
              cardRegistrationURL: registrationData.url,
              preregistrationData: registrationData.preregistrationData,
              accessKey: registrationData.accessKey,
              Id: registrationData.id
            });
            mangoPay.cardRegistration.registerCard({
              cardNumber: $scope.formData.cardNumber,
              cardExpirationDate: $scope.formData.cardExpiration.replace('/', ''),
              cardCvx: $scope.formData.cardCvx,
              cardType: 'CB_VISA_MASTERCARD'
            }, saveCard, handleError);
          }, handleError);
        }
      }

      var allowOnlyDigits = function(ev) {
        if (!ev.key.match(/Backspace|Tab|Paste|\/|[0-9]/i)) {
          ev.preventDefault();
          return false;
        }
      }

      $scope.expiryOnKeyUp = function(ev) {
        if (ev.key === '/' && $scope.formData.cardExpiration.length < 3) {
          $scope.formData.cardExpiration = '0' + $scope.formData.cardExpiration;
        } else if (ev.key === 'Backspace' && $scope.formData.cardExpiration.length === 2) {
          $scope.formData.cardExpiration = $scope.formData.cardExpiration.substr(0, 1);
        } else if ($scope.formData.cardExpiration.length === 2) {
          $scope.formData.cardExpiration = $scope.formData.cardExpiration.replace(/([0-9]{2})([0-9]*)/, '$1/$2');
        }
      }
      $scope.expiryOnKeyDown = allowOnlyDigits;
      $scope.numberOnKeyDown = allowOnlyDigits;
      $scope.cvcOnKeyDown = allowOnlyDigits;

      function handleError(error) {
        var messageId = 'CARD_PAGE.UNKNOWN_ERROR_MESSAGE';
        if (error.data) {
          error = error.data;
        }

        if (error.ResultCode) {
          switch (error.ResultCode) {
            case MangopayCardRegistrationErrorEnum.INVALID_CARD_NUMBER:
              messageId = 'CARD_PAGE.INVALID_CARD_NUMBER_ERROR_MESSAGE';
              break;
            case MangopayCardRegistrationErrorEnum.INVALID_EXPIRY_DATE:
              messageId = 'CARD_PAGE.INVALID_CARD_EXPIRY_DATE_ERROR_MESSAGE';
              break;
            case MangopayCardRegistrationErrorEnum.INVALID_CVV:
              messageId = 'CARD_PAGE.INVALID_CARD_CVV_ERROR_MESSAGE';
              break;
            case MangopayCardRegistrationErrorEnum.WRONG_HOLDER_NAME:
              messageId = 'CARD_PAGE.INVALID_CARD_HOLDER_NAME_ERROR_MESSAGE';
              break;
          }
        }

        Alert.show($translate.instant(messageId)).result.finally(function() {
          angular.element('.modal-footer button.btn-primary').removeClass('disabled').prop('disabled', false);
        });
      }
    }
  ]);
})(mangoPay, Kushki);

/**
 * @typedef ICompositeMenuItemModalControllerScope
 * @prop {() => void} cancel
 * @prop {ng.IDeferred} deferred
 * @prop {(viewItem: any) => any} getViewItem
 * @prop {(menuItem: any) => boolean} isSelected
 * @prop menuItem
 * @prop {() => void} save
 * @prop {(menuItemId: string) => void} toggleItem
 * @prop viewItem
 */

var CompositeMenuItemModalController = (function() {
  var app = angular.module('priorWeb');
  /**
   * @param {ICompositeMenuItemModalControllerScope & ng.IScope} $scope
   * @param {ng.translate.ITranslateService} $translate
   * @param {ng.ui.bootstrap.IModalInstanceService} $uibModalInstance
   * @param {Alert} Alert
   */
  function CompositeMenuItemModalController(
    $scope, $translate, $uibModalInstance, Alert, CompositeMenuItemViewModel, CustomizedCompositeMenuItemModel,
    ModalMenuItemsRepository, MenuItemViewModel, MenuItemsRepository
  ) {
    function getCustomizedItem() {
      if ($scope.menuItem.isSelectionValid(ModalMenuItemsRepository.getAll())) {
        var selectedItems = ModalMenuItemsRepository.getAll(),
            data = $scope.menuItem.getRawData();

        data.MenuItem.editable = false;
        data.MenuItem.standalone = false;
        data.MenuItem.menu_item_id = data.MenuItem.id;
        data.MenuItem.id = null;
        data.ChildMenuItem = selectedItems.map(function(selectedItem) {
          return { id: selectedItem.getId() };
        });

        return CustomizedCompositeMenuItemModel.build(data);
      } else {
        return false;
      }
    }

    $scope.viewItem = CompositeMenuItemViewModel.build($scope.menuItem);

    $scope.save = function() {
      var item = getCustomizedItem();
      if (item !== false) {
        $scope.deferred.resolve(item);
        $uibModalInstance.close();
      } else {
        var invalidRuleSets = $scope.menuItem.getInvalidRules(ModalMenuItemsRepository.getAll());
        Alert.show(
          $translate.instant('COMPOSITE_MENU_ITEM_MODAL.PICK_LABEL') + ': <br />' +
            invalidRuleSets.map(function(invalidRuleSet) {
              return '<ul>' + invalidRuleSet.rules.map(function(rule) {
                var ruleMessage = '';
                if (rule.minCount >= 0 && rule.maxCount > 0) {
                  ruleMessage =
                    '<li>' +
                    $translate.instant('COMPOSITE_MENU_ITEM_MODAL.COMPOSITE_RULE_ERROR_MESSAGE', {
                      exactRule: rule.minCount === rule.maxCount,
                      minCount: rule.minCount,
                      maxCount: rule.maxCount,
                      sectionName: rule.sectionName
                    }, 'messageformat') +
                    '</li>';
                }
                return ruleMessage;
              }).filter(/** @param {string} message */ function(message) { return message; }).join('') + '</ul>';
            }).join($translate.instant('COMPOSITE_MENU_ITEM_MODAL.OR_LABEL') + '<br />')
        );
      }
    };

    $scope.cancel = function() {
      $uibModalInstance.dismiss();
    };

    $scope.getViewItem = function(item) {
      return MenuItemViewModel.build(item);
    };

    $scope.toggleItem = function(menuItemId) {
      if (ModalMenuItemsRepository.has(menuItemId)) {
        ModalMenuItemsRepository.remove(menuItemId);
      } else {
        ModalMenuItemsRepository.save(MenuItemsRepository.get(menuItemId));
      }
    };

    $scope.isSelected = function(menuItem) {
      return ModalMenuItemsRepository.has(menuItem.getId());
    };

    $scope.$on(
      'modal.closing',
      /**
       * @param {ng.IAngularEvent} __event__
       * @param __resultOrReason__ - If modal is being closed the result passed to the closing method, otherwise the
       *                             reason passed to the dismiss method
       * @param {boolean} closing - True if the modal is being closed, false if it is being dismissed
       */
      function(__event__, __resultOrReason__, closing) {
        ModalMenuItemsRepository.removeAll();

        if (!closing) {
          $scope.deferred.reject();
        }
      }
    );
  }

  app.controller('CompositeMenuItemModalController', [
    '$scope', '$translate', '$uibModalInstance', 'Alert', 'CompositeMenuItemViewModel',
    'CustomizedCompositeMenuItemModel', 'ModalMenuItemsRepository', 'MenuItemViewModel', 'MenuItemsRepository',
    CompositeMenuItemModalController
  ]);

  return CompositeMenuItemModalController;
})();
CompositeMenuItemModalController = undefined;

/**
 * @typedef IGenericMenuItemModalControllerScope
 * @prop {() => void} cancel
 * @prop {ng.IDeferred} deferred
 * @prop {(viewItem: any) => any} getViewItem
 * @prop {(menuItem: any) => boolean} isSelected
 * @prop menuItem
 * @prop {() => void} save
 * @prop {(menuItemId: string) => void} toggleItem
 * @prop viewItem
 */

var GenericMenuItemModalController = (function() {
  var app = angular.module('priorWeb');

  /**
   * @param {IGenericMenuItemModalControllerScope & ng.IScope} $scope
   * @param {ng.translate.ITranslateService} $translate
   * @param {ng.ui.bootstrap.IModalInstanceService} $uibModalInstance
   * @param {Alert} Alert
   */
  function GenericMenuItemModalController(
    $scope, $translate, $uibModalInstance, Alert, GenericMenuItemViewModel, MenuItemViewModel, MenuItemsRepository,
    SpecificMenuItemModel
  ) {
    var selectedItem;

    function getSpecificItem() {
      var data = selectedItem.getRawData();

      data.MenuItem.menu_item_id = data.MenuItem.id;
      data.MenuItem.id = null;
      data.MenuItem.generic_id = $scope.menuItem.getId();
      data.MenuItem.name = $scope.menuItem.getName() + ' ' + data.MenuItem.name;

      return SpecificMenuItemModel.build(data);
    }

    $scope.viewItem = GenericMenuItemViewModel.build($scope.menuItem);

    $scope.save = function() {
      if (selectedItem) {
        var item = getSpecificItem();
        $scope.deferred.resolve(item);
        $uibModalInstance.close();
      } else {
        Alert.show($translate.instant('GENERIC_MENU_ITEM_MODAL.PICK_AN_OPTION_MESSAGE'));
      }
    };

    $scope.cancel = function() {
      $uibModalInstance.dismiss();
    };

    $scope.getViewItem = function(item) {
      return MenuItemViewModel.build(item);
    };

    $scope.toggleItem = function(menuItemId) {
      selectedItem = MenuItemsRepository.get(menuItemId);
    };

    $scope.isSelected = function(menuItem) {
      return selectedItem && selectedItem.getId() === menuItem.getId();
    };

    $scope.$on(
      'modal.closing',
      /**
       * @param {ng.IAngularEvent} __event__
       * @param __resultOrReason__ - If modal is being closed the result passed to the closing method, otherwise the
       *                             reason passed to the dismiss method
       * @param {boolean} closing - True if the modal is being closed, false if it is being dismissed
       */
      function(__event__, __resultOrReason__, closing) {
        selectedItem = null;

        if (closing) {
          $scope.deferred.reject();
        }
      });
  }

  app.controller('GenericMenuItemModalController', [
    '$scope', '$translate', '$uibModalInstance', 'Alert', 'GenericMenuItemViewModel', 'MenuItemViewModel',
    'MenuItemsRepository', 'SpecificMenuItemModel',
    GenericMenuItemModalController
  ]);

  return GenericMenuItemModalController;
})();
GenericMenuItemModalController = undefined;

(function() {
  var app = angular.module('priorWeb');

  /**
   * @typedef ILoginControllerScope
   * @property {() => void} cancel
   * @property {string[]} domains
   * @property {() => void} facebookLogin
   * @property {ng.IFormController} form
   * @property {string} formClass
   * @property {{ [param: string]: any }} formData
   * @property {() => string[]} getEmailDomains
   * @property {() => boolean} isLogin
   * @property {() => boolean} isResetPassword
   * @property {() => void} login
   * @property {() => void} register
   * @property {() => void} requestPasswordReset
   * @property {(isLogin: boolean) => void} setLogin
   * @property {(isResetPassword: boolean) => void} setResetPassword
   * @property {(suggestion: string) => void} setSuggestion
   * @property {(suggestion: string) => void} setSuggestionReset
   * @property {array} suggestions
   */

  app.controller('LoginController', [
    '$scope', '$translate', '$uibModalInstance', 'Alert', 'CurrentOrder', 'CurrentUser', 'FacebookLogin',
    'FacebookLoginErrorEnum', 'User', 'trackGAViews', 'GoogleAnalytics',
    /**
     * @param {ILoginControllerScope & ng.IScope} $scope
     * @param {ng.translate.ITranslateService} $translate
     * @param {ng.ui.bootstrap.IModalInstanceService} $uibModalInstance
     * @param {Alert} Alert
     * @param {CurrentUser} CurrentUser
     * @param {typeof FacebookLoginErrorEnum} FacebookLoginErrorEnum
     * @param {UserResource} User
     * @param {string[]} trackGAViews
     * @param {GoogleAnalyticsWrapper} googleAnalytics
     */
    function(
      $scope, $translate, $uibModalInstance, Alert, CurrentOrder, CurrentUser, FacebookLogin,
      FacebookLoginErrorEnum, User, trackGAViews, googleAnalytics
    ) {
      var isLogin = true;
      var isResetPassword = false;

      /**
       * @param {ng.IHttpResponse<{ code: number, message: string }>} result
       */
      function handleRegistrationError(result) {
        switch (result.status) {
        case 403:
          Alert.show(result.data.message);
          break;
        default:
          Alert.show('Unknown error!');
          break;
        }
      }

      /**
       * @param {ng.IHttpResponse} result
       */
      function handleRequestResetPasswordError(result) {
        var message;
        switch (result.status) {
        case 404:
          message = 'LOGIN_MODAL.USER_NOT_FOUND_ERROR';
          break;
        default:
          message = 'LOGIN_MODAL.UNKNOWN_ERROR';
          break;
        }

        $translate(message).then(
          /**
           * @param {string} message
           */
          function(message) {
            Alert.show(message);
          }
        );
      }

      function handleRequestResetPasswordSuccess() {
        $translate('LOGIN_MODAL.RESET_PASSWORD_LINK_SENT').then(
          /**
           * @param {string} message
           */
          function (message) {
            Alert.show(message, 'ALERT_MODAL.SUCCESS_TITLE');
          }
        );
        $uibModalInstance.close();
      }

      /**
       * @param {UserModel} user
       * @param {ng.IHttpHeadersGetter} __responseHeaders__
       */
      function handleUserRegistered(user, __responseHeaders__) {
        CurrentUser.set(user);
        $uibModalInstance.close();
      }

      function login() {
        User.authenticate(
          {},
          $scope.formData,
          /**
           * @param {UserModel} user
           * @param {ng.IHttpHeadersGetter} __headers__
           */
          function authenticationSuccess(user, __headers__) {
            CurrentUser.set(user);
            $uibModalInstance.close();
            googleAnalytics.setUserId(user.getId());
          },
          /**
           * @param {ng.IHttpResponse<{ code: number, message: string }>} httpResponse
           */
          function authenticationError(httpResponse) {
            Alert.show(httpResponse.data.message);
          }
        );
      }

      function register() {
        User.register($scope.formData, handleUserRegistered, handleRegistrationError);
      }

      function requestPasswordReset() {
        User.requestPasswordReset(
          $scope.formData,
          handleRequestResetPasswordSuccess,
          handleRequestResetPasswordError
        );
      }

      $scope.$on('$viewContentLoaded', function(){
        trackGAViews.forEach(function(propertyId) {
          googleAnalytics.trackView(propertyId, 'login');
        });
      });

      $scope.cancel = function() {
        $uibModalInstance.dismiss({
          code: 4201
        });
      };

      $scope.facebookLogin = function() {
        FacebookLogin.login().then(
          function facebookLoginSuccess() {
            if (CurrentUser.isSet()) {
              $uibModalInstance.close();
              googleAnalytics.setUserId(CurrentUser.get().getId());
            }
          },
          /**
           * @param {any} error
           */
          function facebookLoginError(error) {
            if (error && error.errorCode != 4201 && error.errorMessage != 'User cancelled dialog') {
              var message;
              if (error.errorCode === FacebookLoginErrorEnum.MISSING_EMAIL_PERMISSION) {
                message = $translate.instant('LOGIN_MODAL.FACEBOOK_LOGIN_ERROR_MODAL_MISSING_EMAIL_PERMISSION_MESSAGE');
              } else {
                message = error.errorMessage ? error.errorMessage :
                  'No pudimos conectar con Facebook. ¿Podrías volver a intentarlo?';
              }
              Alert.show(message);
            }
          }
        );
      };

      $scope.formData = {
        User: {
          email: null,
          password: null
        }
      };

      $scope.isInstagram = function() {
        return window.navigator.userAgent.indexOf('Instagram') > -1;
      };

      $scope.isLogin = function() {
        return isLogin;
      };

      $scope.isResetPassword = function() {
        return isResetPassword;
      };

      $scope.login = function() {
        $scope.formClass = 'ng-submitted';
        if ($scope.form.$valid) {
          login();
        }
      };

      $scope.register = function() {
        $scope.formClass = 'ng-submitted';
        if ($scope.form.$valid) {
          register();
        }
      };

      $scope.requestPasswordReset = function() {
        $scope.formClass = 'ng-submitted';
        if ($scope.form.$valid) {
          requestPasswordReset();
        }
      };

      $scope.setLogin = function(newValue) {
        isLogin = newValue;
      };

      $scope.setResetPassword = function(newValue) {
        if (newValue) {
          $scope.formData = {
            email: null,
            restaurantId: CurrentOrder.getRestaurantId()
          };
        } else {
          $scope.formData = {
            User: {
              email: null,
              password: null
            }
          };
        }

        isResetPassword = newValue;
      };

      $scope.setSuggestion = function(suggestion) {
        $scope.formData.User.email = suggestion
      }

      $scope.setSuggestionReset = function(suggestion) {
        $scope.formData.email = suggestion
      }
    }
  ]);
})();

/**
 * @typedef IMenuControllerScope
 * @prop {() => boolean} allowOrder
 * @prop {array} categories
 * @prop {(event: Event, viewItem: any) => void} customizeItem
 * @prop {(event: Event, viewItem: any) => void} decreaseItemQuantity
 * @prop {(category: any) => any} getCategoryView
 * @prop {(item: any) => any} getViewItem
 * @prop {boolean} hasMenuImages
 * @prop {(event: Event, viewItem: any) => void} increaseItemQuantity
 * @prop {{[key: string]: any}} items
 * @prop {number} navbarOrderDiscount
 * @prop {string} nextStartTime
 * @prop {number} orderTotal
 * @prop {() => void} processOrder
 * @prop {(event: Event, viewItem: any, customizedViewItem: any) => void} removeCustomizedItem
 * @prop {(event: Event, viewItem: any, specificViewItem: any) => void} removeSpecificItem
 * @prop {string} restaurantDeliveryCharge
 * @prop {string} restaurantOpeningHours
 * @prop restaurantView
 * @prop {(event: Event, viewItem: any) => void} specifyItem
 * @prop {(categoryId: string) => void} scrollTo
 * @prop {(event: Event, viewItem: any) => void} toggleItem
 */

var MenuController = (function() {
  /**
   * @param {ng.IAnchorScrollService} $anchorScroll
   * @param {ng.ILocationService} $location
   * @param {IPriorWebRootScope & ng.IRootScopeService} $rootScope
   * @param {ng.route.IRouteParamsService} $routeParams
   * @param {ng.IQService} $q
   * @param {IMenuControllerScope & IPriorWebRootScope & ng.IScope} $scope
   * @param {ng.ITimeoutService} $timeout
   * @param {ng.translate.ITranslateService} $translate
   * @param {ng.ui.bootstrap.IModalService} $uibModal
   * @param {typeof ActionEnum} ActionEnum
   * @param {Alert} Alert
   * @param {CurrentOrder & BaseRepository} CurrentOrder
   * @param {CurrentUser} CurrentUser
   * @param {typeof FbPixelEventEnum} FbPixelEventEnum
   * @param {State} State
   * @param {string[]} trackGAViews
   * @param {GoogleAnalyticsWrapper} googleAnalytics
   * @param {LoginModal} loginModal
   */
  function MenuController(
    $anchorScroll, $location, $rootScope, $routeParams, $q, $scope, $timeout, $translate, $uibModal, ActionEnum,
    Alert, Category, CategoryViewModel, CategoriesRepository, Currency, CurrentOrder, CurrentUser,
    CompositeMenuItemViewModel, EditableCompositeMenuItemViewModel, FbPixelEventEnum, FbPixel, GenericMenuItemViewModel,
    MenuItem, MenuItemType, MenuItemsRepository, NonStandaloneMenuItemViewModel, OrderItemMapper, OrderStatusEnum,
    RestaurantsRepository, RestaurantViewModel, RuntimeError, SpecificMenuItemViewModel, StandaloneMenuItemViewModel,
    State, trackGAViews, googleAnalytics, loginModal
  ) {
    var menuItemId = $routeParams.menuItemId,
        resetPasswordToken = $routeParams.resetPasswordToken,
        restaurantId = $routeParams.restaurantId,
        restaurant;

    $scope.categories = [];
    $scope.items = {};

    $scope.$on('$viewContentLoaded', function() {
      trackGAViews.forEach(function(propertyId) {
        googleAnalytics.trackView(propertyId, 'menu');
      });
      FbPixel.track('ViewContent');
    });

    $scope.loadedRestaurants.then(function() {
      restaurant = RestaurantsRepository.get(restaurantId);
      Currency.set(restaurant.getCurrency());
      State.dispatch({ type: ActionEnum.SET_RESTAURANT_ID, restaurantId: restaurantId });

      if (!restaurantId || !restaurant || !restaurant.isActive()) {
        $location.url('/');
        return;
      }

      $scope.restaurantDeliveryCharge = restaurant.getDeliveryCharge();
      $scope.nextStartTime = restaurant.getNextStartTime();
      $scope.restaurantView = RestaurantViewModel.build(restaurant);
      $scope.restaurantOpeningHours = $scope.restaurantView.getOpeningHours() ||
        $translate.instant('NAVBAR.RESTAURANT_CLOSED_LABEL');
      $scope.hasMenuImages = restaurant.hasMenuImages();
    });

    function allowOrder() {
      return restaurant && restaurant.allowOrder();
    }

    function refreshMenu() {
      var categoriesDeferred = $q.defer(),
          menuItemsDeferred = $q.defer(),
          promises = [categoriesDeferred.promise, menuItemsDeferred.promise];
      Category.query({
        restaurantId: restaurantId
      }).$promise
        .then(function(categories) {
          CategoriesRepository.query({
            restaurantId: restaurantId
          }).forEach(function(category) {
            CategoriesRepository.remove(category.getId());
          });
          CategoriesRepository.saveAll(categories);
          $scope.categories = categories;
          categoriesDeferred.resolve();
        })
        .catch(function(err) {
          if (err.status !== 304) {
            console.log(err);
          }
        });
      MenuItem.query({
        restaurantId: restaurantId
      }).$promise
        .then(function(menuItems) {
          MenuItemsRepository.query({
            restaurantId: restaurantId
          }).forEach(function(menuItem) {
            if (menuItem.getType() !== MenuItemType.CUSTOMIZED &&
                menuItem.getType() !== MenuItemType.SPECIFIC) {
              MenuItemsRepository.remove(menuItem.getId());
            }
          });
          MenuItemsRepository.saveAll(menuItems);
          menuItemsDeferred.resolve();
        })
        .catch(function(err) {
          if (err.status !== 304) {
            console.log(err);
          }
        });

      $q.all(promises).then(function() {
        displayMenu();
      });
    }

    function customizeItem(menuItem) {
      /** @type {ICompositeMenuItemModalControllerScope & ng.IScope} */
      var modalScope = ($rootScope.$new());
      modalScope.menuItem = menuItem;
      modalScope.deferred = $q.defer();

      $uibModal.open({
        animation: true,
        templateUrl: 'templates/editable-composite-menu-item-modal.html',
        controller: 'CompositeMenuItemModalController',
        scope: modalScope
      });

      return modalScope.deferred.promise;
    }

    function displayMenu() {
      $scope.items = {
        0: getItems(0)
      };
      $scope.categories = CategoriesRepository.getForRestaurant(restaurantId);
      $scope.categories.forEach(function(category) {
        var categoryId = category.getId();
        $scope.items[categoryId] = getItems(categoryId);
      });
    }

    function login() {
      var promise;
      if (!CurrentUser.isSet()) {
        promise = loginModal.open();
      } else {
        promise = $q.resolve();
      }

      return promise;
    }

    function getItems(categoryId) {
      return MenuItemsRepository.getStandaloneForRestaurantAndCategory(restaurantId, categoryId);
    }

    function saveOrderItem(menuItem) {
      var orderItem = OrderItemMapper.map(menuItem);
      CurrentOrder.save(orderItem);
      FbPixel.track(FbPixelEventEnum.ADD_TO_CART);
      updateOrderTotal();
    }

    function specifyItem(menuItem) {
      /** @type {{menuItem: any, deferred: ng.IDeferred} & ng.IScope} */
      var modalScope = ($rootScope.$new());
      modalScope.menuItem = menuItem;
      modalScope.deferred = $q.defer();
      $uibModal.open({
        animation: true,
        templateUrl: 'templates/generic-menu-item-modal.html',
        controller: 'GenericMenuItemModalController',
        scope: modalScope,
      });

      return modalScope.deferred.promise;
    }

    function updateOrderTotal() {
      var updateValues = function() {
        $scope.orderTotal = CurrentOrder.getTotal() - CurrentOrder.getDeliveryCharge().value;
        $scope.navbarOrderDiscount = CurrentOrder.getDiscount() + CurrentOrder.getItemsDiscount();
      };
      if (CurrentUser.isSet()) {
        CurrentOrder.applyPromos().finally(updateValues);
      } else {
        updateValues.call(this);
      }
    }

    $q.all([$rootScope.loadedCategories, $rootScope.loadedItems]).then(function() {
      displayMenu();
      refreshMenu();
      updateOrderTotal();

      if (resetPasswordToken) {
        /** @type {{ token: string} & ng.IScope} */
        var modalScope = ($rootScope.$new());
        modalScope.token = resetPasswordToken;
        $uibModal.open({
          animation: true,
          controller: 'ResetPasswordModalController',
          keyboard: false,
          scope: modalScope,
          templateUrl: 'templates/reset-password-modal.html'
        }).result
          .then(
            /**
             * @param { { message: string } } result
             */
            function(result) {
              Alert.show(result.message).closed.then(function() {
                login().then(function() {
                  $location.url('/menu/' + restaurantId);
                });
              });
            }
          )
          .catch(
            /**
             * @param { { message: string } } result
             */
            function(result) {
              Alert.show(result.message);
              $location.url('/menu/' + restaurantId);
            }
          );
      }
    });

    $scope.allowOrder = allowOrder;

    $scope.loadedEverything.then(function() {
      if (!CurrentOrder.hasItems() || CurrentOrder.getRestaurantId() !== restaurant.getId() ||
          CurrentOrder.getStatus() != OrderStatusEnum.CREATED) {
        CurrentOrder.init(restaurant);
      }

      if (CurrentOrder.getPaymentType() === null) {
        State.dispatch({ type: ActionEnum.SET_PAYMENT_TYPE, paymentType: restaurant.getDefaultPaymentType() });
      }

      $scope.customizeItem = function(event, viewItem) {
        event.preventDefault();
        event.stopPropagation();

        var parentItem = MenuItemsRepository.get(viewItem.getId());
        customizeItem(parentItem).then(function(menuItem) {
          viewItem.setIsSelected(true);
          var prevQuantity = 0,
              alreadySelectedItem = MenuItemsRepository.getCustomizedFor(parentItem.getId())
              .reduce(
                function(selectedItem, item) {
                  if (menuItem.getDescription() === item.getDescription()) {
                    selectedItem = item;
                  }

                  return selectedItem;
                },
                null
              );

          if (alreadySelectedItem) {
            prevQuantity = CurrentOrder.getItemQuantity(alreadySelectedItem.getId());
            var orderItem = CurrentOrder.query({ menuItemId: alreadySelectedItem.getId() })[0];
            State.dispatch({
              type: ActionEnum.REMOVE_SPECIFIC_ITEM,
              itemId: alreadySelectedItem.getId()
            });
            CurrentOrder.remove(orderItem.getId());
            MenuItemsRepository.remove(alreadySelectedItem.getId());
          }

          MenuItemsRepository.save(menuItem);
          saveOrderItem(menuItem);
          State.dispatch({
            type: ActionEnum.SAVE_CUSTOMIZED_ITEM,
            item: menuItem.getRawData()
          });

          for (var i = 0; i < prevQuantity; i++) {
            CurrentOrder.increaseItemQuantity(menuItem.getId());
          }
        });
      };

      $scope.decreaseItemQuantity = function(event, viewItem) {
        event.preventDefault();
        event.stopPropagation();

        var menuItemId = viewItem.getId(),
            newQuantity;
        CurrentOrder.decreaseItemQuantity(menuItemId);
        newQuantity = CurrentOrder.getItemQuantity(menuItemId);
        if (newQuantity > 0) {
          viewItem.setQuantity(newQuantity);
        } else {
          viewItem.setIsSelected(false);
        }
        updateOrderTotal();
      };

      $scope.getCategoryView = function(category) {
        return CategoryViewModel.build(category);
      };

      $scope.getViewItem = function(item) {
        var viewItem;
        var quantity;

        switch (item.getType()) {
        case MenuItemType.COMPOSITE:
        case MenuItemType.CUSTOMIZED:
          if (item.isEditable()) {
            viewItem = EditableCompositeMenuItemViewModel.build(item);
            quantity = item.getCustomizedItems().length;
          } else {
            viewItem = CompositeMenuItemViewModel.build(item);
            quantity = CurrentOrder.getItemQuantity(item.getId());
          }
          break;
        case MenuItemType.GENERIC:
          viewItem = GenericMenuItemViewModel.build(item);
          quantity = item.getSpecificItems().reduce(function(quantity, specificItem) {
            return quantity + CurrentOrder.getItemQuantity(specificItem.getId());
          }, 0);
          break;
        case MenuItemType.STANDALONE:
          viewItem = StandaloneMenuItemViewModel.build(item);
          quantity = CurrentOrder.getItemQuantity(item.getId());
          break;
        case MenuItemType.NON_STANDALONE:
          viewItem = NonStandaloneMenuItemViewModel.build(item);
          quantity = CurrentOrder.getItemQuantity(item.getId());
          break;
        case MenuItemType.SPECIFIC:
          viewItem = SpecificMenuItemViewModel.build(item);
          quantity = CurrentOrder.getItemQuantity(item.getId());
          break;
        default:
          throw RuntimeError.build({
            message: 'Unknown or unsupported menu item type "' + item.getType() + '"'
          });
        }
        viewItem.setQuantity(quantity);
        viewItem.setIsSelected(quantity > 0);

        return viewItem;
      };

      $scope.increaseItemQuantity = function(event, viewItem) {
        event.preventDefault();
        event.stopPropagation();

        var menuItemId = viewItem.getId();
        CurrentOrder.increaseItemQuantity(menuItemId);
        var newQuantity = CurrentOrder.getItemQuantity(menuItemId);
        viewItem.setQuantity(newQuantity);
        updateOrderTotal();
      };

      $scope.processOrder = function() {
        if (allowOrder()) {
          if (CurrentOrder.hasItems()) {
            login().then(function() {
              if (CurrentUser.isSet()) {
                $location.url('/payment');
              }
            });
          } else {
            Alert.show('Elige por lo menos un item del menu');
          }
        }
      };

      $scope.removeCustomizedItem = function(event, viewItem, customizedViewItem) {
        event.preventDefault();
        event.stopPropagation();

        var customizedItem = MenuItemsRepository.get(customizedViewItem.getId()),
            parentItem = MenuItemsRepository.get(viewItem.getId()),
            currentQuantity = CurrentOrder.getItemQuantity(customizedItem.getId());

        if (currentQuantity === 1) {
          var orderItem = CurrentOrder.query({ menuItemId: customizedItem.getId() })[0];
          CurrentOrder.remove(orderItem.getId());
          State.dispatch({
            type: ActionEnum.REMOVE_CUSTOMIZED_ITEM,
            itemId: customizedItem.getId()
          });
          MenuItemsRepository.remove(customizedItem.getId());
        } else {
          CurrentOrder.decreaseItemQuantity(customizedItem.getId());
          customizedViewItem.setQuantity(currentQuantity - 1);
        }

        if (parentItem.getCustomizedItems().length === 0) {
          viewItem.setIsSelected(false);
          viewItem.setQuantity(0);
        }
        updateOrderTotal();
      };

      $scope.removeSpecificItem = function(event, viewItem, specificViewItem) {
        event.preventDefault();
        event.stopPropagation();

        var specificItem = MenuItemsRepository.get(specificViewItem.getId()),
            parentItem = MenuItemsRepository.get(viewItem.getId()),
            currentQuantity = CurrentOrder.getItemQuantity(specificItem.getId());
        if (currentQuantity === 1) {
          var orderItem = CurrentOrder.query({ menuItemId: specificItem.getId() })[0];
          CurrentOrder.remove(orderItem.getId());
          State.dispatch({
            type: ActionEnum.REMOVE_SPECIFIC_ITEM,
            itemId: specificItem.getId()
          });
          MenuItemsRepository.remove(specificItem.getId());
        } else {
          CurrentOrder.decreaseItemQuantity(specificItem.getId());
          specificViewItem.setQuantity(currentQuantity - 1);
        }
        if (parentItem.getSpecificItems().length === 0) {
          viewItem.setIsSelected(false);
          viewItem.setQuantity(0);
        }
        updateOrderTotal();
      };

      $scope.scrollTo = function(categoryId) {
        $anchorScroll('category-' + categoryId);
      };

      $scope.specifyItem = function(event, viewItem) {
        event.preventDefault();
        event.stopPropagation();

        var parentItem = MenuItemsRepository.get(viewItem.getId());
        specifyItem(parentItem).then(function(menuItem) {
          var alreadySelectedItem =
              parentItem.getSpecificItems().reduce(function(alreadySelectedItem, specific) {
                if (!alreadySelectedItem && specific.getSelectionId() == menuItem.getSelectionId()) {
                  alreadySelectedItem = specific;
                }

                return alreadySelectedItem;
              }, null),
              prevQuantity = 0;

          // TODO: Make this less hacky
          if (alreadySelectedItem) {
            prevQuantity = CurrentOrder.getItemQuantity(alreadySelectedItem.getId());
            var orderItem = CurrentOrder.query({ menuItemId: alreadySelectedItem.getId() })[0];
            State.dispatch({
              type: ActionEnum.REMOVE_SPECIFIC_ITEM,
              itemId: alreadySelectedItem.getId()
            });
            CurrentOrder.remove(orderItem.getId());
            MenuItemsRepository.remove(alreadySelectedItem.getId());
          }
          MenuItemsRepository.save(menuItem);
          viewItem.setIsSelected(true);
          State.dispatch({
            type: ActionEnum.SAVE_SPECIFIC_ITEM,
            item: menuItem.getRawData()
          });
          saveOrderItem(menuItem);
          for (var i = 0; i < prevQuantity; i++) {
            CurrentOrder.increaseItemQuantity(menuItem.getId());
          }
        });
      };

      $scope.toggleItem = function(event, viewItem) {
        event.preventDefault();
        event.stopPropagation();

        var menuItemId = viewItem.getId();
        if (CurrentOrder.has(menuItemId)) {
          var orderItem = CurrentOrder.query({ menuItemId: menuItemId })[0];
          CurrentOrder.remove(orderItem.getId());
          viewItem.setIsSelected(false);
        } else {
          var menuItem = MenuItemsRepository.get(menuItemId);
          saveOrderItem(menuItem);
          viewItem.setIsSelected(true);
          viewItem.setQuantity(1);
        }
        updateOrderTotal();
      };

      $timeout(function() {
        if (menuItemId) {
          $anchorScroll('menuItem-' + menuItemId);
        }
      }, 1000);
    });
  }

  var app = angular.module('priorWeb');
  app.controller('MenuController', [
    '$anchorScroll', '$location', '$rootScope', '$routeParams', '$q', '$scope', '$timeout', '$translate', '$uibModal',
    'ActionEnum', 'Alert', 'Category', 'CategoryViewModel', 'CategoriesRepository', 'Currency', 'CurrentOrder',
    'CurrentUser', 'CompositeMenuItemViewModel', 'EditableCompositeMenuItemViewModel', 'FbPixelEventEnum', 'FbPixel',
    'GenericMenuItemViewModel', 'MenuItem', 'MenuItemType', 'MenuItemsRepository', 'NonStandaloneMenuItemViewModel',
    'OrderItemMapper', 'OrderStatusEnum', 'RestaurantsRepository', 'RestaurantViewModel', 'RuntimeError',
    'SpecificMenuItemViewModel', 'StandaloneMenuItemViewModel', 'State', 'trackGAViews', 'GoogleAnalytics',
    'loginModal',
    MenuController
  ]);

  return MenuController;
})();
MenuController = undefined;

(function() {
  /**
   * @typedef IOrderedControllerScope
   * @prop {(status: object) => Date} getAcceptedDate
   * @prop {(status: object) => string} getRejectReason
   * @prop {(order: object) => boolean} isAccepted
   * @prop {(order: object) => boolean} isCreated
   * @prop {(order: object) => boolean} isDelivery
   * @prop {(order: object) => boolean} isRejected
   * @prop {(order: object) => boolean} isUnconfirmed
   * @prop {object} order
   * @prop {string} orderId
   */

  var app = angular.module('priorWeb');
  app.controller('OrderedController', [
    '$routeParams', '$scope', '$timeout', 'Currency', 'CurrentOrder', 'FbPixelEventEnum', 'FbPixel',
    'OrderLocationEnum', 'OrderStatusEnum', 'RejectReasonEnum', 'RestaurantsRepository', 'Order',
    'moment', 'trackGAViews', 'trackGATransactions', 'trackGtagConversions', 'GoogleAnalytics', 'GoogleGlobalTag',
    /**
     * @param {ng.route.IRouteParamsService} $routeParams
     * @param {IOrderedControllerScope & IPriorWebRootScope & ng.IScope} $scope
     * @param {ng.ITimeoutService} $timeout
     * @param {any} moment
     * @param {string[]} trackGAViews
     * @param {string[]} trackGATransactions
     * @param {string[]} trackGtagConversions
     * @param {GoogleAnalyticsWrapper} googleAnalytics
     * @param {GoogleGlobalTag} googleGlobalTag
     */
    function(
      $routeParams, $scope, $timeout, Currency, CurrentOrder, FbPixelEventEnum, FbPixel,
      OrderLocationEnum, OrderStatusEnum, RejectReasonEnum, RestaurantsRepository, Order,
      moment, trackGAViews, trackGATransactions, trackGtagConversions, googleAnalytics, googleGlobalTag
    ) {
      var pollForResponseTimer = null,
          pollForResponseMultiplier = 1,
          restaurantId = $routeParams.restaurantId,
          restaurant;

      $scope.$on('$destroy', function() {
        clearPollForResponse();
      });

      $scope.$on('$viewContentLoaded', function(){
        trackGAViews.forEach(function(propertyId) {
          googleAnalytics.trackView(propertyId, 'ordered');
        });
      });

      $scope.orderId = $routeParams.orderId;
      $scope.order = null;

      CurrentOrder.clear();

      $scope.loadedEverything.then(function() {
        restaurant = RestaurantsRepository.get(restaurantId);
        Currency.set(restaurant.getCurrency());
        pollForResponse();
      });

      function is(order, orderStatus) {
        return order && order.getStatus() === orderStatus;
      }

      $scope.isAccepted = function(order) {
        return is(order,  OrderStatusEnum.ACCEPTED);
      };

      $scope.isCreated = function(order) {
        return is(order,  OrderStatusEnum.CREATED);
      };

      $scope.isRejected = function(order) {
        return is(order,  OrderStatusEnum.REJECTED);
      };

      $scope.isUnconfirmed = function(order) {
        return is(order,  OrderStatusEnum.UNCONFIRMED);
      };

      $scope.isDelivery = function(order) {
        return parseInt(order.getWhere()) === OrderLocationEnum.DELIVERY;
      };

      $scope.getAcceptedDate = function(orderStatus) {
        var zone = moment.tz.guess();
        return moment.tz(orderStatus.getExtra(), 'UTC').tz(zone).toDate();
      }

      $scope.getRejectReason = function(orderStatus) {
        /** @type {string} */
        var reason;
        switch (orderStatus.getRejectReason()) {
          case RejectReasonEnum.BUSY:
            reason = 'ORDERED_PAGE.BUSY_REJECT_REASON';
            break;
          case RejectReasonEnum.MISSING_ITEM:
            reason = 'ORDERED_PAGE.MISSING_ITEM_REJECT_REASON';
            break;
          default:
            reason = 'ORDERED_PAGE.GENERIC_REJECT_REASON';
            break;
        }

        return reason;
      };

      function clearPollForResponse() {
        if (pollForResponseTimer) {
          $timeout.cancel(pollForResponseTimer);
          pollForResponseTimer = null;
        }
      }

      function pollForResponse() {
        var queryParams = {
          restaurantId: restaurantId,
          sortOrder: 'DESC-ASC'
        };
        Order.get({ id: $scope.orderId }, {}, function(order) {
          $scope.order = order;
          if (order.getStatus() === OrderStatusEnum.CREATED ||
              order.getStatus() === OrderStatusEnum.UNCONFIRMED) {
            scheduleNextPoll();
          }
          if (order.getStatus() === OrderStatusEnum.ACCEPTED) {
            FbPixel.track(FbPixelEventEnum.PURCHASE, {
              value: order.getTotal() / Currency.getMultiplier(),
              currency: Currency.get()
            });

            trackGATransactions.forEach(function(propertyId) {
              googleAnalytics.trackTransaction(
                propertyId,
                order.getId(),
                order.getRestaurantId(),
                order.getTotal() / Currency.getMultiplier(),
                Currency.get(),
                '0',
                '0'
              );
            });

            trackGtagConversions.forEach(function(conversionId) {
              googleGlobalTag.trackConversion(conversionId, order.getId());
            });
          }
        }, function(response) {
          if (response.status == 304) {
            scheduleNextPoll();
          }
        });
      }

      function scheduleNextPoll() {
        pollForResponseTimer = $timeout(
          pollForResponse,
          pollForResponseMultiplier * 1000
        );
        pollForResponseMultiplier = (pollForResponseMultiplier * 2) % 30;
      }
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.controller('PaymentModuleController', [
    '$rootScope', 'PaymentProviderEnum', 'RestaurantsRepository', 'State',
    /**
     * @param {ng.IRootScopeService} $rootScope
     * @param {PaymentProviderEnum} PaymentProviderEnum
     * @param {RestaurantsRepository} RestaurantsRepository
     * @param {State} State
     */
    function PaymentModuleController($rootScope, PaymentProviderEnum, RestaurantsRepository, State) {
      var ctrl = this,
          state = State.get(),
          restaurantId = state.app.restaurantId,
          restaurant;

      ctrl.paymentType = {
        value: state.currentOrder.paymentType
      };

      ctrl.updatePaymentType = function updatePaymentType() {
        ctrl.paymentTypeUpdated({ paymentType: ctrl.paymentType.value });
      };

      ctrl.isKushkiOrder = function isKushkiOrder() {
        return ctrl.paymentType.value === PaymentProviderEnum.KUSHKI;
      }

      ctrl.isMangoPayOrder = function isMangoPayOrder() {
        return ctrl.paymentType.value === PaymentProviderEnum.MANGOPAY;
      }

      ctrl.isCurrentOrderPaymentType = function isCurrentOrderPaymentType(paymentType) {
        return paymentType === ctrl.paymentType.value;
      };

      $rootScope.loadedEverything.then(function() {
        ctrl.updatePaymentType();

        restaurant = RestaurantsRepository.get(restaurantId);

        ctrl.availablePaymentProviders = restaurant.getPaymentProviders();
        ctrl.severalExistingPaymentOptions = ctrl.availablePaymentProviders.length > 1;
        ctrl.paymentSectionDisplayed = ctrl.severalExistingPaymentOptions ||
          ctrl.isMangoPayOrder() ||
          ctrl.isKushkiOrder();
      });
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.controller('PaymentController', [
    'GoogleAnalytics', '$localStorage', '$location', '$q', '$rootScope', '$scope',
    '$translate', '$uibModal', '$window', 'ActionEnum', 'Alert', 'Card', 'CheckDeliveryAddressStatusEnum',
    'CompositeMenuItemViewModel', 'Currency', 'CurrentOrder', 'CurrentUser', 'EventEnum', 'FbPixelEventEnum', 'FbPixel',
    'MangopayPreauthErrorEnum', 'MenuItemType', 'MenuItemViewModel', 'MenuItemsRepository', 'OrderErrorCodeEnum',
    'OrderLocationEnum', 'OrderStatusEnum', 'PaymentErrorEnum', 'PaymentProviderEnum', 'RedsysResponseEnum',
    'RestaurantViewModel', 'RestaurantsRepository', 'RuntimeError', 'State', 'User', 'WhenDayEnum', 'trackGAViews',
    /**
     * @param {GoogleAnalyticsWrapper} googleAnalytics
     * @param {string[]} trackGAViews
     * @param {ng.ui.bootstrap.IModalService} $uibModal
     */
    function (
      googleAnalytics, $localStorage, $location, $q, $rootScope, $scope, $translate, $uibModal,
      $window, ActionEnum, Alert, Card, CheckDeliveryAddressStatusEnum,  CompositeMenuItemViewModel, Currency, CurrentOrder,
      CurrentUser, EventEnum, FbPixelEventEnum, FbPixel, MangopayPreauthErrorEnum, MenuItemType, MenuItemViewModel,
      MenuItemsRepository, OrderErrorCodeEnum, OrderLocationEnum, OrderStatusEnum, PaymentErrorEnum, PaymentProviderEnum,
      RedsysResponseEnum, RestaurantViewModel, RestaurantsRepository, RuntimeError, State, User, WhenDayEnum, trackGAViews
    ) {
      var restaurantId,
          restaurant,
          pendingPreAuthId = State.get().currentOrder.pendingPreAuthId,
          returnPreAuthId = $location.search().preAuthorizationId,
          redsysCode = $location.search().redsysCode;

      $scope.$on(EventEnum.RUNNING_REQUESTS_COUNTER_UPDATED, function(__event__, runningRequests) {
        if (runningRequests === 0) {
          angular.element('#action button.btn-primary').removeClass('disabled').prop('disabled', false);
        } else {
          angular.element('#action button.btn-primary').addClass('disabled').prop('disabled', true);
        }
      });

      $scope.$on('prior.places-autocomplete-updated', function(event, addressComponents) {
        var zipCode = addressComponents.reduce(function(zip_code, component) {
          if (component.types.indexOf('postal_code') != -1) {
            return component.long_name;
          } else {
            return zip_code;
          }
        }, '');

        $scope.$apply(function() {
          $scope.formData.zipCode = zipCode;
          CurrentOrder.setZipCode(zipCode);
        });
      });

      $scope.orderDiscount = 0;
      $scope.orderTotal = 0;
      $scope.isDelivery = isDelivery;

      $scope.$on('$viewContentLoaded', function() {
        trackGAViews.forEach(function(propertyId) {
          googleAnalytics.trackView(propertyId, 'payment');
        });
        FbPixel.track(FbPixelEventEnum.INIT_CHECKOUT);
      });

      $scope.loadedEverything.then(function() {
        restaurantId = CurrentOrder.getRestaurantId();
        restaurant = RestaurantsRepository.get(restaurantId);

        if (!CurrentUser.isSet() || !restaurantId || !restaurant || !CurrentOrder.hasItems() ||
            CurrentOrder.getStatus() != OrderStatusEnum.CREATED) {
          $location.url('/');
          return;
        }

        Currency.set(restaurant.getCurrency());

        if (pendingPreAuthId) {
          if (!returnPreAuthId || pendingPreAuthId === returnPreAuthId) {
            CurrentOrder.confirmPreauth(pendingPreAuthId)
              .then(goToOrders)
              .catch(function(httpResponse) {
                var errorCode = httpResponse.data && httpResponse.data.code ? httpResponse.data.code.toString() : '0';

                showPaymentErrorAndRedirect(errorCode);
                CurrentOrder.cancelPreauth(pendingPreAuthId);
              });
          } else {
            CurrentOrder.cancelPreauth($location.search().preAuthorizationId);
            CurrentOrder.cancelPreauth(pendingPreAuthId);
            showPaymentErrorAndRedirect(MangopayPreauthErrorEnum.SECURE_MODE_CANCELED);
          }
        } else if (redsysCode) {
          showPaymentErrorAndRedirect(redsysCode);
        }

        refreshUserInfo();

        $scope.restaurantView = RestaurantViewModel.build(restaurant);
        $scope.restaurantOpeningHours = $scope.restaurantView.getOpeningHours() ||
          $translate.instant('NAVBAR.RESTAURANT_CLOSED_LABEL');
        $scope.restaurantDeliveryCharge = restaurant.getDeliveryCharge();
        $scope.deliveryCharge = CurrentOrder.getDeliveryCharge();
        $scope.$watch(
          function () {
            return CurrentUser.get().getName();
          },
          function(newName, oldName) {
            if (newName !== oldName) {
              $scope.formData.name = newName;
            }
          }
        );
        $scope.$watch(
          function watchDeliveryChangeValue(scope) {
            return scope.deliveryCharge.value;
          },
          applyPromosAndUpdateOrderTotal
        );

        if (CurrentOrder.getPromoCode()) {
          checkPromoCode();
        } else {
          applyPromosAndUpdateOrderTotal();
        }

        $scope.cardNumber = CurrentUser.get().getCard() ? CurrentUser.get().getCard().getNumber().substring(12) : null;

        $scope.addCard = function(alertText) {
          var modalScope = $rootScope.$new(),
              openModal = function() {
                var modal = $uibModal.open({
                  animation: true,
                  templateUrl: 'templates/card-modal.html',
                  controller: 'CardController',
                  scope: modalScope,
                  keyboard: false,
                  backdrop: 'static'
                });

                modal.result.then(function(newCard) {
                  var user = CurrentUser.get();
                  user.setCard(newCard);
                  CurrentUser.set(user);
                  $scope.cardNumber = newCard.getNumber();
                }, function() {
                  // Closed
                });
              };

          modalScope['paymentType'] = CurrentOrder.getPaymentType();

          if (alertText !== undefined) {
            Alert.show(alertText).result.finally(function() {
              openModal();
            });
          } else {
            openModal();
          }
        };

        $scope.clearPromoCode = clearPromoCode;

        $scope.deleteCard = function() {
          var cardId = CurrentUser.get().getCard().getId();
          Card.delete({ id :cardId }).$promise.then(function() {
            refreshUserInfo();
            Alert.show($translate.instant('PAYMENT_PAGE.CARD_DELETED_MESSAGE'));
          }).catch(function() {
            Alert.show($translate.instant('PAYMENT_PAGE.CARD_COULD_NOT_BE_DELETED_MESSAGE'));
          });
        }

        $scope.getView = function(menuItem) {
          var view;

          switch (menuItem.getType()) {
            case MenuItemType.STANDALONE:
            case MenuItemType.SPECIFIC:
            case MenuItemType.COMPOSITE:
              view = 'templates/order-menu-item.html';
              break;
            case MenuItemType.CUSTOMIZED:
              view = 'templates/composite-order-menu-item.html';
              break;
            default:
              throw RuntimeError.build({
                message: 'Unknown or unsupported menu item type "' + menuItem.getType() + '"'
              });
          }

          return view;
        };

        $scope.getViewItem = function(menuItem) {
          var viewItem;

          switch (menuItem.getType()) {
            case MenuItemType.CUSTOMIZED:
              viewItem = CompositeMenuItemViewModel.build(menuItem);
              break;
            case MenuItemType.SPECIFIC:
            case MenuItemType.STANDALONE:
            case MenuItemType.COMPOSITE:
              viewItem = MenuItemViewModel.build(menuItem);
              break;
            default:
              throw RuntimeError.build({
                message: 'Unknown or unsupported menu item type "' + menuItem.getType + '"'
              });
          }
          var quantity = CurrentOrder.getItemQuantity(menuItem.getId());
          viewItem.setQuantity(quantity);
          viewItem.setIsSelected(quantity > 0);

          return viewItem;
        };

        $scope.items = CurrentOrder.getAll().reduce(function(items, orderItem) {
          items.push(MenuItemsRepository.get(orderItem.getMenuItemId()));

          return items;
        }, []);

        $scope.saveOrder = function() {
          $scope.paymentFormClass = 'ng-submitted';
          CurrentOrder.setComments($scope.formData.comments);
          CurrentOrder.setPhone($scope.formData.phone);

          if (isDelivery()) {
            CurrentOrder
              .setDeliveryAddress($scope.formData.address)
              .then(function() {
                return CurrentOrder.setZipCode($scope.formData.zipCode);
              })
              .then(applyPromosAndUpdateOrderTotal);
          }

          validate().then(function() {
            if (CurrentOrder.getStatus() === OrderStatusEnum.CREATED) {
              reviewOrder();
            } else {
              goToOrders();
            }
          })
            .catch(function(error) {
              switch (error) {
                case 'invalidDeliveryAddress':
                  Alert.show($translate.instant('PAYMENT_PAGE.DELIVERY_ADDRESS_INVALID'));
                  break;
                case 'invalidForm':
                  var firstError;
                  for (var key in $scope.paymentForm.$error) {
                    firstError = $scope.paymentForm.$error[key][0].$name;
                    break;
                  }
                  angular.element('[name="' + firstError + '"]').focus();
                  break;
                case 'invalidName':
                  Alert.show($translate.instant('PAYMENT_PAGE.INVALID_NAME_MESSAGE'));
                  break;
                case 'invalidTotal':
                  var minimumOrder = restaurant.getMinimumOrder(CurrentOrder.getWhere()),
                      difference = minimumOrder - (CurrentOrder.getItemsTotal());

                  Alert.show(
                    $translate.instant('PAYMENT_PAGE.MINIMUM_ORDER_NOT_MET_MESSAGE', {
                      minimumOrder: Currency.format(minimumOrder),
                      difference: Currency.format(difference)
                    })
                  );
                  break;
                case 'missingCard':
                  var text = $translate.instant('PAYMENT_PAGE.MISSING_CARD_MESSAGE', {
                    hasCredit: $scope.credit > 0
                  }, 'messageformat');
                  $scope.addCard(text);
                  break;
                case 'missingPaymentType':
                  showPaymentTypeError(CurrentOrder.getPaymentType());
                  break;
                case 'notCoveredDeliveryAddress':
                  Alert.show($translate.instant('PAYMENT_PAGE.NOT_COVERED_DELIVERY_ADDRESS_ERROR_MODAL_MESSAGE'));
              }
            });
        };

        $scope.savePromoCode = savePromoCode;

        $scope.updateComments = function() {
          CurrentOrder.setComments($scope.formData.comments);
        };

        $scope.updateDeliveryAddress = function() {
          CurrentOrder.setDeliveryAddress($scope.formData.address);
          applyPromosAndUpdateOrderTotal();
        };

        $scope.updateName = function() {
          var user = CurrentUser.get();
          user.setName($scope.formData.name);
          CurrentUser.set(user);
        }

        $scope.updateOrderWhen = function() {
          CurrentOrder.setWhen($scope.formData.when ? $scope.formData.when.value : null);
        };

        $scope.updateOrderWhenDay = function() {
          $scope.formData.when = null;
          $scope.updateOrderWhen();
          $scope.whenOptions = getPickUpTimes(
            restaurant.getMinimumPrepTime(CurrentOrder.getWhere()),
            restaurant.hasAsapOrder()
          );
        };

        $scope.updateOrderWhere = function() {
          CurrentOrder.setWhere($scope.formData.where.value);
          if (CurrentOrder.getWhere() !== OrderLocationEnum.DELIVERY) {
            CurrentOrder.setDeliveryAddress(undefined);
            CurrentOrder.setZipCode(undefined);
          } else {
            CurrentOrder.setDeliveryAddress($scope.formData.address);
            CurrentOrder.setPhone($scope.formData.phone);
            CurrentOrder.setZipCode($scope.formData.zipCode);
          }

          $scope.formData.when = null;
          $scope.formData.whenDay = {
            value: WhenDayEnum.TODAY
          };
          $scope.updateOrderWhen();
          $scope.whenOptions = getPickUpTimes(
            restaurant.getMinimumPrepTime(CurrentOrder.getWhere()),
            restaurant.hasAsapOrder()
          );

          applyPromosAndUpdateOrderTotal();
        };

        $scope.updatePaymentType = function(paymentType) {
          CurrentOrder.setPaymentType(paymentType);
          refreshUserInfo();
        }

        $scope.updatePhone = function() {
          CurrentOrder.setPhone($scope.formData.phone);
        };

        $scope.updateZipCode = function() {
          CurrentOrder.setZipCode($scope.formData.zipCode);
          applyPromosAndUpdateOrderTotal();
        };

        $scope.formData = {
          whenDay: {
            value: isToday(CurrentOrder.getWhen()) ?
              WhenDayEnum.TODAY : WhenDayEnum.TOMORROW
          },
          address: CurrentOrder.getDeliveryAddress(),
          name: CurrentUser.get().getName(),
          phone: CurrentOrder.getPhone(),
          zipCode: CurrentOrder.getZipCode(),
          comments: CurrentOrder.getComments()
        };

        if (restaurant.hasNextDayOrder() && restaurant.isOpenTomorrow()) {
          $scope.whenDayOptions = [
            {
              name: $translate.instant('PAYMENT_PAGE.TODAY_LABEL'),
              value: WhenDayEnum.TODAY
            },
            {
              name: $translate.instant('PAYMENT_PAGE.TOMORROW_LABEL'),
              value: WhenDayEnum.TOMORROW
            }];
        }

        $scope.whenOptions = getPickUpTimes(
          restaurant.getMinimumPrepTime(CurrentOrder.getWhere()),
          restaurant.hasAsapOrder()
        );

        $scope.whereOptions = restaurant.getWhereOptions();
        if ($scope.whereOptions.length === 0) {
          $scope.whereOptions = [
            {
              name: 'PAYMENT_PAGE.TAKEOUT_LABEL',
              value: OrderLocationEnum.TAKEOUT
            },
            {
              name: 'PAYMENT_PAGE.DINING_IN_LABEL',
              value: OrderLocationEnum.DINING_IN
            }
          ];
        }

        if (isValidWhen()) {
          $scope.formData['when'] = { value: CurrentOrder.getWhen() };
        } else {
          $scope.formData['when'] = null;
          $scope.updateOrderWhen();
        }

        if ($scope.whereOptions.map(function(option) {
          return option.value;
        }).indexOf(CurrentOrder.getWhere()) == -1) {
          CurrentOrder.setWhere(restaurant.getDefaultOrderType());
        }

        $scope.formData['where'] = { value: CurrentOrder.getWhere() };

        $scope.promoCodeFormData = {
          promoCode: ''
        };
      });

      function applyPromosAndUpdateOrderTotal() {
        updateOrderTotal();
        CurrentOrder.applyPromos().finally(function() {
          updateOrderTotal();
        });
      }

      function checkPromoCode() {
        function handleInvalidCode() {
          Alert.show($translate.instant('PAYMENT_PAGE.INVALID_COUPON_ERROR'));
          clearPromoCode();
        }

        CurrentOrder.checkPromoCode()
          .then(function checkPromoCodeSuccess(validCode) {
            if (validCode) {
              $scope.promoCode = CurrentOrder.getPromoCode();
              applyPromosAndUpdateOrderTotal();
            } else {
              handleInvalidCode();
            }
          })
          .catch(function checkPromoCodeFailure() {
            handleInvalidCode();
          });
      }

      function clearPromoCode() {
        CurrentOrder.setPromoCode('');
        $scope.promoCodeFormData.promoCode= '';
        $scope.promoCode = '';
        applyPromosAndUpdateOrderTotal();
      }

      function getNextQuarterHour(time) {
        var millis = time.getMilliseconds() + time.getSeconds() * 1000 + time.getMinutes() * 60000,
            nextQuarterTime = new Date(time);

        nextQuarterTime.setMilliseconds(0);
        nextQuarterTime.setSeconds(0);
        nextQuarterTime.setMinutes(Math.ceil(millis / 900000) * 15);

        return nextQuarterTime;
      }

      function getPickUpTimes(prepTime, hasAsapOrder) {
        var date = new Date(),
            closingTimes,
            nextTimeInterval,
            openingTimes,
            times = [];

        if ($scope.formData.whenDay.value == WhenDayEnum.TOMORROW) {
          date.setDate(date.getDate() + 1);
          date.setHours(0, 0, 0, 0);
        } else if (hasAsapOrder) {
          times.push({ name: $translate.instant('PAYMENT_PAGE.ASAP_OPTION_LABEL'), value: -1 });
        }

        nextTimeInterval = getNextQuarterHour(new Date(date.getTime() + prepTime * 60000));
        openingTimes = restaurant.getOpeningTimesOnDay(date);
        closingTimes = restaurant.getClosingTimesOnDay(date);

        if (openingTimes.length > 0) {
          times = openingTimes.reduce(function(times, openingTime, index) {
            if (closingTimes[index].getTime() > date.getTime()) {
              var firstTime = new Date(Math.max(openingTime.getTime(), nextTimeInterval.getTime())),
                  lastTime = new Date(closingTimes[index]);

              for (
                var currentTime = new Date(firstTime);
                currentTime <= lastTime;
                currentTime.setMinutes(currentTime.getMinutes() + 15)
              ) {
                var time = withLeadingZero(currentTime.getHours()) + ':' + withLeadingZero(currentTime.getMinutes());
                times.push({
                  name: time,
                  value: currentTime.getFullYear() + '-' +
                    withLeadingZero(currentTime.getMonth() + 1) + '-' +
                    withLeadingZero(currentTime.getDate()) + ' ' +
                    withLeadingZero(currentTime.getHours()) + ':' +
                    withLeadingZero(currentTime.getMinutes()) + ':' +
                    withLeadingZero(currentTime.getSeconds())
                });
              }
            }

            return times;
          }, times);
        }

        return times;
      }

      function goToOrders() {
        var orderId = CurrentOrder.getId();
        CurrentOrder.clear();
        $location.url('/ordered/' + restaurant.getId() + '/' + orderId);
        return;
      }

      function handleMangoPayPreauthError(httpResponse) {
        var errorCode = httpResponse.data && httpResponse.data.code ? httpResponse.data.code.toString() : '0';

        if (errorCode === MangopayPreauthErrorEnum.SECURE_MODE_REQUIRED) {
          State.dispatch({
            type: ActionEnum.REQUEST_3DS_AUTHORIZATION,
            preAuthId: httpResponse.data.preAuthId
          });
          $localStorage.state = State.get();
          $window.location = httpResponse.data.url;
        } else {
          showPaymentError(errorCode);
        }
      }

      function isToday(when) {
        return !when || when == -1 || (new RegExp('^[0-9]{4}-[0-9]{2}-0?' + (new Date()).getDate())).test(when);
      }

      function isValidWhen() {
        return CurrentOrder.getWhen() == -1 || getPickUpTimes(
          restaurant.getMinimumPrepTime(CurrentOrder.getWhere()),
          restaurant.hasAsapOrder()
        ).map(function(time) {
          return time.value;
        }).indexOf(CurrentOrder.getWhen()) != -1;
      }

      function reviewOrder() {
        var modal = $uibModal.open({
          animation: true,
          templateUrl: 'templates/review-order-modal.html',
          controller: 'ReviewOrderModalController',
          scope: $rootScope.$new(),
          keyboard: false,
          backdrop: 'static',
          size:'md'
        });

        modal.result.then(function(result) {
          saveOrder();
        }, function() {
          if (isDelivery() && $scope.formData.comments && $scope.formData.comments.indexOf('\t\n') > -1) {
            var COMMENTS_REGEX = /^([^\t\n]*)(\t\n)?(.*)$/s;
            $scope.formData.address += ', ' + $scope.formData.comments.replace(COMMENTS_REGEX, '$1');
            $scope.formData.comments = $scope.formData.comments.replace(COMMENTS_REGEX, '$3');
            CurrentOrder.setComments($scope.formData.comments);
            CurrentOrder
              .setDeliveryAddress($scope.formData.address)
              .then(applyPromosAndUpdateOrderTotal);
          }
        });
      }

      function showPaymentErrorAndRedirect(errorCode) {
        State.dispatch({
          type: ActionEnum.CLEAR_3DS_AUTHORIZATION
        });
        showPaymentError(errorCode).finally(function() {
          $location.url('/payment');
        });
      }

      function showPaymentError(errorCode) {
        var messageId;

        switch (errorCode) {
          case MangopayPreauthErrorEnum.TRANSACTION_REFUSED:
          case MangopayPreauthErrorEnum.TRANSACTION_REFUSED_AMOUNT_LIMIT:
          case MangopayPreauthErrorEnum.TRANSACTION_REFUSED_BY_TERMINAL:
          case MangopayPreauthErrorEnum.TRANSACTION_REFUSED_CARD_LIMIT_REACHED:
          case MangopayPreauthErrorEnum.TRANSACTION_REFUSED_DO_NOT_HONOUR:
          case MangopayPreauthErrorEnum.MAXIMUM_AMOUNT_EXCEEDED:
          case RedsysResponseEnum.DENIED_BY_ISSUER:
            messageId = 'PAYMENT_PAGE.TRANSACTION_DENIED_CREDIT_CARD_ERROR';
            break;
          case MangopayPreauthErrorEnum.CARD_EXPIRED:
          case RedsysResponseEnum.EXPIRED_CARD:
            messageId = 'PAYMENT_PAGE.EXPIRED_CREDIT_CARD_ERROR';
            break;
          case MangopayPreauthErrorEnum.CARD_INACTIVE:
          case MangopayPreauthErrorEnum.CARD_NOT_ACTIVE:
            messageId = 'PAYMENT_PAGE.INACTIVE_CREDIT_CARD_ERROR';
            break;
          case MangopayPreauthErrorEnum.SECURE_MODE_CANCELED:
          case MangopayPreauthErrorEnum.SECURE_MODE_NOT_AVAILABLE:
          case MangopayPreauthErrorEnum.SECURE_MODE_EXPIRED:
          case MangopayPreauthErrorEnum.SECURE_MODE_INCOMPATIBLE_CARD:
          case MangopayPreauthErrorEnum.SECURE_MODE_UNENROLLED_CARD:
          case MangopayPreauthErrorEnum.SECURE_MODE_FAILED:
            messageId = 'PAYMENT_PAGE.3DS_CREDIT_CARD_ERROR';
            break;
          case RedsysResponseEnum.WRONG_CVV2_CVC2:
            messageId = 'PAYMENT_PAGE.REDSYS_WRONG_CVV2_CVC2_ERROR_MESSAGE';
            break;
          case RedsysResponseEnum.WRONG_EXPIRY_DATE:
            messageId = 'PAYMENT_PAGE.REDSYS_WRONG_EXPIRY_DATE_ERROR_MESSAGE';
            break;
          default:
            messageId = 'PAYMENT_PAGE.PREAUTH_GENERIC_ERROR_MESSAGE';
            break;
        }

        refreshUserInfo();
        angular.element('#action button.btn-primary').addClass('disabled').prop('disabled', true);
        return Alert.show($translate.instant(messageId)).result.finally(function() {
          angular.element('#action button.btn-primary').removeClass('disabled').prop('disabled', false);
        });
      }

      function handleSaveError(httpResponse) {
        var messageId = 'PAYMENT_PAGE.GENERIC_SAVE_ORDER_ERROR',
            params = {};
        if (httpResponse.data && httpResponse.data.code) {
          switch (httpResponse.data.code) {
            case OrderErrorCodeEnum.ORDERING_NOT_ALLOWED:
              messageId = 'PAYMENT_PAGE.RESTAURANT_CLOSED_LABEL';
              params['nextStartTime'] = restaurant.getNextStartTime();
              params['openToday'] = !!restaurant.getNextStartTime();
              params['use_messageformat'] = true;
              break;
            case OrderErrorCodeEnum.DELIVERY_NOT_AVAILABLE:
              messageId = 'PAYMENT_PAGE.DELIVERY_UNAVAILABLE_LABEL';
              params['use_messageformat'] = true;
              break;
            default:
              messageId = httpResponse.data.messageId ? httpResponse.data.messageId : messageId;
              break;
          }
        }

        angular.element('#action button.btn-primary').addClass('disabled').prop('disabled', true);
        Alert.show($translate.instant(messageId, params, params.use_messageformat ? 'messageformat' : undefined)).result.finally(function() {
          angular.element('#action button.btn-primary').removeClass('disabled').prop('disabled', false);
        });
      }

      function isDelivery() {
        return parseInt(CurrentOrder.getWhere()) === OrderLocationEnum.DELIVERY;
      }

      function isKushkiOrder() {
        return CurrentOrder.getPaymentType() === PaymentProviderEnum.KUSHKI;
      }

      function isMangoPayOrder() {
        return CurrentOrder.getPaymentType() === PaymentProviderEnum.MANGOPAY;
      }

      function isPrePayOrder() {
        return [
          PaymentProviderEnum.CASH,
          PaymentProviderEnum.RAPPIPAY,
          PaymentProviderEnum.TPV
        ].indexOf(CurrentOrder.getPaymentType()) === -1;
      }

      function loadCreditCardAndCreditInfo() {
        if (isKushkiOrder() || isMangoPayOrder()) {
          $scope.cardNumber = CurrentUser.get().getCard() ? CurrentUser.get().getCard().getNumber().substring(12) : null;

          if (isMangoPayOrder()) {
            $scope.credit = Math.min(CurrentUser.get().getCredit(), CurrentOrder.getTotal());
            $scope.creditLeft = Math.max(CurrentUser.get().getCredit() - CurrentOrder.getTotal(), 0);
          }
        }
        applyPromosAndUpdateOrderTotal();
      }

      function preauthOrder() {
        switch(CurrentOrder.getPaymentType()) {
          case PaymentProviderEnum.KUSHKI:
            CurrentOrder.preauth().then(goToOrders).catch(showPaymentError);
            break;
          case PaymentProviderEnum.MANGOPAY:
            CurrentOrder.preauth().then(goToOrders).catch(handleMangoPayPreauthError);
            break;
          case PaymentProviderEnum.REDSYS:
            CurrentOrder.prepareRedsysAuthorization()
              .then(function(authorizationData) {
                var form = angular.element('<form action="' + authorizationData.getUrl() + '" method="POST">\
                      <input type="hidden" name="Ds_SignatureVersion" value="' + authorizationData.getSignatureType() + '"/>\
                      <input type="hidden" name="Ds_MerchantParameters" value="' + authorizationData.getTransactionData() + '"/>\
                      <input type="hidden" name="Ds_Signature" value="' + authorizationData.getSignature() + '"/>\
                  </form>');
                angular.element('body').append(form);
                form.submit();
              });
            break;
          default:
            showPaymentTypeError(CurrentOrder.getPaymentType());
          break;
        }
      }

      function showPaymentTypeError(paymentType) {
        if (typeof paymentType === undefined) {
          console.log('Missing Payment Type');
          showPaymentError(PaymentErrorEnum.MISSING_PAYMENT_TYPE);
        } else {
          console.log(paymentType + ' is not a valid payment type');
          showPaymentError(PaymentErrorEnum.INCORRECT_PAYMENT_TYPE);
        }
      }

      function refreshUserInfo() {
        User.get(
          {
            id: CurrentUser.get().getId(),
            currency: restaurant.getCurrency(),
            payment_provider: CurrentOrder.getPaymentType()
          },
          {},
          function(user) {
            user.setName(CurrentUser.get().getName());
            CurrentUser.set(user);
            loadCreditCardAndCreditInfo();
          }
        );
      }

      function saveName()
      {
        var user = CurrentUser.get();
        return User.saveName({
          id: user.getId(),
          name: $scope.formData.name
        }).$promise
            .then(function(result) {
            return result.success;
        });
      }

      function saveOrder() {
        return CurrentOrder.saveOrder().then(
          function() {
            if (
              CurrentUser.get().isVerified() && CurrentOrder.getTotal() > 0 &&
                isPrePayOrder() && CurrentOrder.getStatus() === OrderStatusEnum.CREATED
            ) {
              preauthOrder();
            } else {
              goToOrders();
            }
          }, handleSaveError);
      }

      function savePromoCode() {
        CurrentOrder.setPromoCode($scope.promoCodeFormData.promoCode);
        checkPromoCode();
      }

      function updateOrderTotal() {
        $scope.restaurantDeliveryCharge = restaurant.getDeliveryCharge();
        $scope.deliveryCharge = CurrentOrder.getDeliveryCharge();
        $scope.orderDiscount = CurrentOrder.getDiscount() + CurrentOrder.getItemsDiscount();
        $scope.orderTotal = CurrentOrder.getTotal() - CurrentUser.get().getCredit();
      }

      function validate() {
        return validateWhen()
          .then(validateForm)
          .then(validateName)
          .then(validateAddress)
          .then(validateCreditCard)
          .then(validateMinimumOrder);
      }

      function validateCreditCard() {
        switch(CurrentOrder.getPaymentType()) {
          case PaymentProviderEnum.KUSHKI:
          case PaymentProviderEnum.MANGOPAY:
            if (CurrentOrder.getTotal() > CurrentUser.get().getCredit() && !$scope.cardNumber) {
              return $q.reject('missingCard');
            }
          case PaymentProviderEnum.CASH:
          case PaymentProviderEnum.RAPPIPAY:
          case PaymentProviderEnum.REDSYS:
          case PaymentProviderEnum.TPV:
            return $q.resolve(true);
          default:
            showPaymentTypeError(CurrentOrder.getPaymentType());
        }
      }

      function validateDeliveryAddress() {
        if (isDelivery()) {
          return CurrentOrder.checkDeliveryAddress().then(function(result) {
            if (result.status === CheckDeliveryAddressStatusEnum.OK && result.predictions.length === 1) {
              var deliveryAddress = result.predictions[0].terms
                  .filter(function(term) {
                    return term.value !== CurrentOrder.getCity() &&
                      term.value !== CurrentOrder.getZipCode() &&
                      term.value !== 'España' &&
                      term.value !== 'Spain';
                  })
                  .map(function(term) {
                    return term.value;
                  })
                  .join(', ');
              var addressUpdated = CurrentOrder.setDeliveryAddress(deliveryAddress);
              $scope.formData.address = deliveryAddress;

              if (result.comment) {
                var currentComments = CurrentOrder.getComments(),
                    newComments = result.comment + '\t\n' + (currentComments ? currentComments : '');
                CurrentOrder.setComments(newComments);
                $scope.formData.comments = newComments;
              }

              return addressUpdated.then(function() {
                return true;
              });
            } else {
              return $q.reject(result.status);
            }
          }).catch(function(status) {
            if (status == CheckDeliveryAddressStatusEnum.NOT_COVERED) {
              return $q.reject('notCoveredDeliveryAddress');
            } else {
              return $q.reject('invalidDeliveryAddress');
            }
          });
        } else {
          $q.resolve(true);
        }
      }

      function validateAddress() {
        if (restaurant.getAddressValidation() && isDelivery()) {
          return validateDeliveryAddress();
        } else {
          return $q.resolve(true);
        }
      }

      function validateForm() {
        if (!$scope.paymentForm.$valid) {
          return $q.reject('invalidForm');
        } else {
          return $q.resolve(true);
        }
      }

      function validateMinimumOrder() {
        if (CurrentOrder.getItemsTotal() > 0 &&
            CurrentOrder.getItemsTotal() <
            restaurant.getMinimumOrder(CurrentOrder.getWhere())
        ) {
          return $q.reject('invalidTotal');
        } else {
          return $q.resolve(true);
        }
      }

      function validateName() {
        return saveName().then(function(success) {
          if (success) {
            return true;
          } else {
            return $q.reject('invalidName');
          }
        });
      }

      function validateWhen() {
        if (isValidWhen()) {
          return $q.resolve(true);
        } else {
          CurrentOrder.setWhen(null);
          $scope.formData.when = null;
          $scope.whenOptions = getPickUpTimes(
            restaurant.getMinimumPrepTime(CurrentOrder.getWhere()),
            restaurant.hasAsapOrder()
          );

          return $q.reject('invalidForm');
        }
      }

      function withLeadingZero(value) {
        return (value < 10 ? '0' : '') + (value + '');
      }
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  /**
   * @typedef ResetPasswordModalScopeExtension
   * @property {Function} cancel
   * @property {ng.IFormController} form
   * @property {string} formClass
   * @property { { [fieldName: string]: any } } formData
   * @property {Function} resetPassword
   * @property {string} token
   */

  /**
   * @typedef {ResetPasswordModalScopeExtension & ng.IScope} ResetPasswordModalScope

   */

  app.controller('ResetPasswordModalController', [
    '$scope', '$translate', '$uibModalInstance', 'User',
    /**
     * @param {ResetPasswordModalScope} $scope
     * @param {ng.translate.ITranslateService} $translate
     * @param {ng.ui.bootstrap.IModalInstanceService} $uibModalInstance
     */
    function ResetPasswordModalController($scope, $translate, $uibModalInstance, User) {
      $scope.formData = {
        new_password: null,
        password_confirmation: null,
        token: $scope.token
      };

      $scope.resetPassword = function resetPassword() {
        $scope.formClass = 'ng-submitted';
        if ($scope.form.$valid) {
          User.resetPassword({}, $scope.formData).$promise
            .then(
              /**
               * @param { { message: string } } response
               */
              function(response) {
                $uibModalInstance.close({
                  message: response.message
                });
              }
            )
            .catch(
              /**
               * @param {ng.IHttpResponse} response
               */
              function(response) {
                $uibModalInstance.dismiss({
                  success: false,
                  message: response.data.message
                });
              }
            );
        }
      };

      $scope.cancel = function cancel() {
        $translate('RESET_PASSWORD_MODAL.USER_CANCELED_ERROR_MESSAGE')
          .then(function(message) {
            $uibModalInstance.dismiss({
              message: message
            });
          });
      };
    }
  ]);
}());

/**
 * @typedef IRestaurantsControllerScope
 * @prop {(restauant: any) => any} getView
 * @prop {Array} restaurants
 */
var RestaurantsController = (function() {
  /**
   * @param {IRestaurantsControllerScope & ng.IScope} $scope
   * @param {typeof ActionEnum} ActionEnum
   * @param {State} State
   * @param {string[]} trackGAViews
   * @param {GoogleAnalyticsWrapper} googleAnalytics
   */
  function RestaurantsController (
    $location, $scope, ActionEnum, CurrentOrder, Location, Restaurant, RestaurantViewModel, RestaurantsRepository,
    State, brandId, trackGAViews, googleAnalytics
  ) {
    State.dispatch({ type: ActionEnum.SET_RESTAURANT_ID, restaurantId: null });

    $scope.restaurants = [];

    CurrentOrder.clear();

    displayRestaurants();
    refreshRestaurants();

    $scope.$on('$viewContentLoaded', function  $on$ViewContentLoaded(){
      trackGAViews.forEach(function(propertyId) {
        googleAnalytics.trackView(propertyId, 'restaurants');
      });
    });

    $scope.getView = function getView(restaurant) {
      return RestaurantViewModel.build(restaurant);
    };

    function displayRestaurants() {
      $scope.restaurants = RestaurantsRepository.getAll().filter(function(restaurant) {
        return restaurant.isActive();
      });

      if ($scope.restaurants.length == 1) {
        $location.url('/menu/' + $scope.restaurants[0].getId());
      }
    }

    function getRestaurants(pos) {
      var params = {};
      if (pos) {
        params = {
          lat: pos.latitude,
          lon: pos.longitude,
          t:pos.timestamp
        };
      }
      params['brand_id'] = brandId;

      Restaurant.query(params).$promise
        .then(function(restaurants) {
          // TODO: Move this to the repository
          RestaurantsRepository.removeAll();
          RestaurantsRepository.saveAll(restaurants);
          displayRestaurants();
        })
        .catch(function(err) {
          if (err.status !== 304) {
            console.log(err);
          }
        });
    }

    function refreshRestaurants() {
      if (Location.enabled()) {
        Location.get().then(function(pos) {
          getRestaurants(pos);
        }, function() {
          getRestaurants(false);
        });
      } else {
        getRestaurants(false);
      }
    }
  }

  var app = angular.module('priorWeb');

  app.controller('RestaurantsController', [
    '$location', '$scope', 'ActionEnum', 'CurrentOrder', 'Location', 'Restaurant', 'RestaurantViewModel',
    'RestaurantsRepository', 'State', 'brandId', 'trackGAViews' ,'GoogleAnalytics',
    RestaurantsController
  ]);

  return RestaurantsController;
})();
RestaurantsController = undefined;

(function() {
  var app = angular.module('priorWeb');

  /**
   * @typedef IReviewOrderModalControllerScope
   * @property orderReviewView
   */

  app.controller('ReviewOrderModalController', [
    '$scope', 'OrderReviewViewModel',
    /**
     * @param {IReviewOrderModalControllerScope & ng.IScope} $scope
     * @param OrderReviewViewModel
     */
    function ($scope, OrderReviewViewModel) {
      $scope.orderReviewView = OrderReviewViewModel.build();
    }
  ]);
}());

/** @typedef ISubHeaderControllerScope
 * @prop {boolean} loggedIn
 * @prop {() => void} login
 * @prop {() => void} logout
 */
var SubHeaderController = (function() {
  /**
   * @param {ng.ILocationService} $location
   * @param {ISubHeaderControllerScope & ng.IScope} $scope
   * @param {CurrentOrder} CurrentOrder
   * @param {CurrentUser} CurrentUser
   * @param {FacebookLogin} FacebookLogin
   * @param {UserResource} User
   * @param {GoogleAnalyticsWrapper} googleAnalytics
   * @param {LoginModal} loginModal
   */
  function SubHeaderController(
    $location, $scope, CurrentOrder, CurrentUser, FacebookLogin, User, googleAnalytics, loginModal
  ) {
    $scope.loggedIn = CurrentUser.isSet();

    $scope.login = function() {
      loginModal.open().finally(function() {
        $scope.loggedIn = CurrentUser.isSet();
      });
    };

    $scope.logout = function() {
      User.logout({}, function() {
        CurrentOrder.clear();
        CurrentUser.clear();
        googleAnalytics.setUserId(null);

        try {
          FacebookLogin.logout();
        } catch(e) {
          // Facebook not logged in, probably
        } finally {
          $location.url('/');
        }
      });
    };
  }

  var app = angular.module('priorWeb');
  app.controller('SubHeaderController', [
    '$location', '$scope', 'CurrentOrder', 'CurrentUser', 'FacebookLogin', 'User', 'GoogleAnalytics', 'loginModal',
    SubHeaderController
  ]);

  return SubHeaderController;
})();
SubHeaderController = undefined;

(function() {
  var app = angular.module('priorWeb');

  app.directive('emailValidate', [
    'User',
    function(User) {
      var domains;
      return {
        link: {
          pre: function preLink(scope, elem, attrs, ctrl) {
            domains = User.emailDomains().$promise;
          },
          post: function postLink(scope, elem, attrs, ctrl) {
            scope.$watch(attrs.ngModel, function(value) {
              domains.then(function (result) {
                Mailcheck.run({
                  email: value,
                  domains: result['domains'],
                  suggested: function(suggestion) {
                      scope.$parent.suggestion = suggestion.full;
                  },
                  empty: function() {
                      scope.$parent.suggestion = null;
                  }
              });
              }).catch(function (error) {
                console.log(error);
              })
              
          });
          }
        }
      };
    }
  ]);
}());
(function() {
  var app = angular.module('priorWeb');

  app.directive('equalTo', [
    function() {
      return {
        link: function(scope, __element__, __attributes__, controller) {
          controller.$validators.equalTo = function(modelValue, viewValue) {
            return modelValue && modelValue === scope['equalTo'];
          };
        },
        require: 'ngModel',
        scope: {
          'equalTo': '=equalTo'
        }
      };
    }
  ]);
}());

(function() {
  var app = angular.module('priorWeb');

  app.directive('priorDynAffix', [
    '$window',
    function($window) {
      return {
        link: function(scope, elem, attributes, __controller__) {
          function adjustWidth() {
            if (params.width) {
              elem.width(angular.element(params.width).width());
            }
          }

          function adjustAffixOffset() {
            var newBottomOffset = angular.element(params.offsetBottom).outerHeight(true),
                newTopOffset = angular.element(params.offsetTop).offset().top;

            if (elem.data('bs.affix')) {
              elem.data('bs.affix').options.offset.bottom = newBottomOffset;
              elem.data('bs.affix').options.offset.top = newTopOffset;
            }
          }

          var params = JSON.parse(attributes.priorDynAffix.replace(/'([^']+)'/g, '"$1"'));

          elem.affix({
            offset: {
              bottom: angular.element(params.offsetBottom).outerHeight(true),
              top: angular.element(params.offsetTop).offset().top
            }
          });

          angular.element($window).on('resize', function() {
            adjustAffixOffset();
            adjustWidth();
          });

          scope.$watch(function() {
            return angular.element(params.offsetBottom).outerHeight(true);
          }, adjustAffixOffset);

          scope.$watch(function() {
            return angular.element(params.offsetTop).offset().top;
          }, adjustAffixOffset);

          elem.on('affix.bs.affix', adjustWidth);
        }
      };
    }
  ]);
})();

var PriorPlacesAutocomplete = (function(angular) {
  /**
   * @param {GooglePlacesAutocomplete} GooglePlacesAutocomplete
   */
  function PriorPlacesAutocomplete(GooglePlacesAutocomplete) {
    this.autocompleteService = GooglePlacesAutocomplete;

    /** @type {JQLite} */
    this.element = null;

    /** @type {google.maps.places.Autocomplete} */
    this.autocomplete = null;
  }

  /** @type {ng.IDirectiveLinkFn} */
  PriorPlacesAutocomplete.prototype.link = function(scope, element, attributes, controller, transclude) {
    this.element = element;
    this.$scope = scope;
    this.autocomplete = this.autocompleteService.autocomplete(
      element.get()[0],
      {
        componentRestrictions: {
          country:'co'
        }
      },
      this.onResult.bind(this),
      ['formatted_address', 'address_components']
    );
  };

  PriorPlacesAutocomplete.prototype.onResult = function() {
    /**
     * @param {google.maps.GeocoderAddressComponent[]} addressComponents
     * @param {string} type
     */
    function getAddressComponent(addressComponents, type) {
      return addressComponents.reduce(function(result, component) {
        if (component.types.indexOf(type) !== -1) {
          return component.long_name;
        } else {
          return result;
        }
      }, '');
    }

    var place = this.autocomplete.getPlace();

    var excludedComponents = [
      getAddressComponent(place.address_components, 'locality'),
      getAddressComponent(place.address_components, 'country')
    ].filter(function(part) { return !!part; });


    this.element.val(place.formatted_address.split(', ').filter(function(part) {
      return !excludedComponents.includes(part);
    }).join(', '));

    this.$scope.$emit('prior.places-autocomplete-updated', place.address_components);
  };

  var app = angular.module('priorWeb');
  app.directive('priorPlacesAutocomplete', [
    'GooglePlacesAutocomplete',
    function(GooglePlacesAutocomplete) {
      return new PriorPlacesAutocomplete(GooglePlacesAutocomplete);
    }
  ]);

  return PriorPlacesAutocomplete;
})(angular);
PriorPlacesAutocomplete = undefined;

(function() {
  var app = angular.module('priorWeb');

  app.filter('priorCurrency', [
    'Currency',
    function(Currency) {
      return function priorCurrency(value, currency) {
        return Currency.format(value, currency);
      };
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.service('OrderItemMapper', [
    'MenuItemType', 'OrderItemModel',
    function(MenuItemType, OrderItemModel) {
      function OrderItemMapper() {
        var nextId = 1;

        function getNextId() {
          return 'order_item_' + nextId++;
        }

        this.map = function mapOrderItemFromMenuItem(menuItem) {
          return OrderItemModel.build({
            OrderMenuItem: {
              id: getNextId(),
              menu_item_id: menuItem.getId(),
              name: menuItem.getName() + (menuItem.getType() === MenuItemType.CUSTOMIZED ?
                                          (' (' + menuItem.getDescription() + ')') : ''),
              price: menuItem.getPrice(),
              quantity: 1
            }
          });
        };
      }

      return new OrderItemMapper();
    }
  ]);
})();

var ActionEnum = (function() {
  var app = angular.module('priorWeb');

  /** @enum {string} */
  var ActionEnum = {
    ADD_ORDER_ITEM: 'saveOrderItem',
    CHANGE_ORDER_ITEM_QUANTITY: 'changeItemQuantity',
    CLEAR_3DS_AUTHORIZATION: 'clear3dsAuthorization',
    CLEAR_ORDER: 'clearOrder',
    CLEAR_RESTAURANT_ID: 'clearRestaurantId',
    REMOVE_ORDER_ITEM: 'removeOrderItem',
    REMOVE_CUSTOMIZED_ITEM: 'removeCustomizedItem',
    REMOVE_SPECIFIC_ITEM: 'removeSpecificItem',
    REQUEST_3DS_AUTHORIZATION: 'request3dsAuthorization',
    SAVE_CUSTOMIZED_ITEM: 'saveCustomizedItem',
    SAVE_SPECIFIC_ITEM: 'saveSpecificItem',
    SET_COMMENTS: 'setComments',
    SET_DELIVERY_ADDRESS: 'setDeliveryAddress',
    SET_ORDER_ID: 'setOrderId',
    SET_PAYMENT_TYPE: 'setPaymentType',
    SET_PHONE: 'setPhone',
    SET_PROMO_CODE: 'setPromoCode',
    SET_RESTAURANT_ID: 'setRestaurantId',
    SET_STATUS: 'setStatus',
    SET_WHEN: 'setWhen',
    SET_WHERE: 'setWhere',
    SET_ZIP_CODE: 'setZipCode'
  };

  app.constant('ActionEnum', ActionEnum);

  return ActionEnum;
})();
ActionEnum = undefined;

var FbPixelEventEnum = (function() {
  /** @enum {string} */
  var FbPixelEventEnum = {
    ADD_PAYMENT_INFO: 'AddPaymentInfo',
    ADD_TO_CART: 'AddToCart',
    INIT_CHECKOUT: 'InitiateCheckout',
    PAGE_VIEW: 'PageView',
    PURCHASE: 'Purchase',
    VIEW_CONTENT: 'ViewContent'
  };

  var app = angular.module('priorWeb');
  app.constant('FbPixelEventEnum', FbPixelEventEnum);

  return FbPixelEventEnum;
})();
FbPixelEventEnum = undefined;

(function() {

  var app = angular.module('priorWeb');

  app.constant('PaymentProviderEnum', {
    CASH: 'cash',
    KUSHKI: 'kushki',
    MANGOPAY: 'mangopay',
    RAPPIPAY: 'rappipay',
    REDSYS: 'redsys',
    TPV: 'tpv'
  });

})();

(function() {
  var app = angular.module('priorWeb');

  app.constant('RedsysResponseEnum', {
    CANCELATION_AUTHORIZED: '0400',
    CARD_NOT_IN_SYSTEM: '0180',
    DENIED_BY_ISSUER: '0190',
    DUPLICATED_ORDER: '0913',
    EXPIRED_CARD: '0101',
    FIRST_PAYMENT_OR_PREAUTH_AUTHORIZED: '0000',
    ISSUER_NOT_AVAILABLE2: '0912',
    ISSUER_NOT_AVAILABLE: '9912',
    LAST_PAYMENT_OR_PREAUTH_AUTHORIZED: '0099',
    NOT_AN_ACTUAL_CARD: '0125',
    OWNER_AUTENTICATION_ERROR: '0184',
    PIN_TRIED_EXCEEDED: '0106',
    REFUND_NOT_ALLOWED: '0950',
    REFUND_OR_CONFIRMATION_AUTHORIZED: '0900',
    SYSTEM_ERROR: '0909',
    TEMPORARY_FAILURE_OR_FRAUD: '0102',
    TPV_NOT_IN_FUC: '0904',
    WITHHOLDING_TEMPORARY_FAILURE_OR_FRAUD: '0202',
    WRONG_CVV2_CVC2: '0129',
    WRONG_EXPIRY_DATE: '0191',
    WRONG_SESSION: '0944'
  });
})();

(function() {
  var app = angular.module('priorWeb');

  app.constant('RedsysStatusEnum', {
    ERROR: 'ERROR',
    OK: 'OK'
  });
})();

(function () {
  var app = angular.module('priorWeb');

  app.factory('WebOrder', [
    '$http', '$resource', 'AuthorizationPreparationModel', 'Order', 'OrderItemModel',
    'OrderItemsRepository', 'OrderModel', 'apiEndpoint',
    function(
      $http, $resource, AuthorizationPreparationModel, Order, OrderItemModel,
      OrderItemsRepository, OrderModel, apiEndpoint
    ) {
      function processAuthorizationPreparation(data, __headersGetter__, status) {
        if (status === 200 && angular.isObject(data)) {
          return AuthorizationPreparationModel.build(data);
        } else {
          return data;
        }
      }

      function buildOrder(data) {
        var order = OrderModel.build(data);
        data.OrderMenuItem.forEach(function(itemData) {
          OrderItemsRepository.save(OrderItemModel.build({
            OrderMenuItem: itemData
          }));
        });
        return order;
      }

      function processOrderData(data, __headersGetter__, status) {
        if (status === 200 && angular.isObject(data)) {
          return buildOrder(data);
        } else {
          return data;
        }
      }

      function getResponseDataProcessors(defaults, specific) {
        return (angular.isArray(defaults) ? defaults : [defaults]).concat(specific);
      }

      var TempOrder = $resource(apiEndpoint + '/orders/:id', {
        id: '@Order.id'
      }, {
        prepareAuthorization: {
          method: 'POST',
          url: apiEndpoint + '/orders/:id/prepareAuthorization',
          params: { id: '@id' },
          transformResponse: getResponseDataProcessors(
            $http.defaults.transformResponse,
            processAuthorizationPreparation
          )
        },
        getOrderDataFromMerchantData: {
          method: 'POST',
          url: apiEndpoint + '/orders/parseMerchantData',
          params: {},
          transformResponse: getResponseDataProcessors(
            $http.defaults.transformResponse,
            processOrderData
          )
        }
      });

      ['prepareAuthorization', 'getOrderDataFromMerchantData'].forEach(function(method) {
        Order[method] = TempOrder[method];
        Order.prototype['$' + method] = TempOrder['$' + method];
      });

      return Order;
    }
  ]);
})();

/**
 * @typedef AppState
 * @property {string} restaurantId
 */

/**
 * @typedef {import("redux").Reducer<AppState, import("redux").AnyAction>} AppReducer
 */

var AppReducerFactory = (function() {

  var app = angular.module('priorWeb');

  /**
   * @param {typeof ActionEnum} ActionEnum
   * @returns {AppReducer}
   */
  function AppReducerFactory(ActionEnum) {
    return function appReducer(state, action) {
      /** @type AppState */
      var newState;
      if (typeof state === 'undefined') {
        newState = { restaurantId: null };
      } else {
        newState = angular.copy(state);
      }

      switch (action.type) {
        case ActionEnum.CLEAR_RESTAURANT_ID:
          newState.restaurantId = null;
          break;
        case ActionEnum.SET_RESTAURANT_ID:
          newState.restaurantId = action.restaurantId;
          break;
      }

      return newState;
    };
  }

  app.factory('AppReducer', [
    'ActionEnum',
    AppReducerFactory
  ]);

  return AppReducerFactory;
})();
AppReducerFactory = undefined;


/**
 * @typedef CurrentOrderState
 * @property {string} [city]
 * @property {string} [comments]
 * @property {string} [deliveryAddress]
 * @property {string} [id]
 * @property {array} items
 * @property {string} paymentType
 * @property {string} [pendingPreAuthId]
 * @property {string} [phone]
 * @property {string} [promoCode]
 * @property {string} status
 * @property {number} when
 * @property {number} where
 * @property {string} [zipCode]
 */

/**
 * @typedef {import("redux").Reducer<CurrentOrderState, import("redux").AnyAction>} CurrentOrderReducer
 */

var CurrentOrderReducerFactory = (function() {
  var app = angular.module('priorWeb');

  /**
   * @param {typeof ActionEnum} ActionEnum
   * @return {CurrentOrderReducer}
   */
  function CurrentOrderReducerFactory(ActionEnum) {
    /** @type CurrentOrderState */
    var DEFAULT_STATE = { items: [], paymentType: null, status: 'created', where: 3, when: -1 };

    return function currentOrderReducer(state, action) {
      var newState;
      if (typeof state === 'undefined') {
        newState = DEFAULT_STATE;
      } else {
        newState = angular.copy(state);
      }

      switch (action.type) {
      case ActionEnum.ADD_ORDER_ITEM:
        newState.items = newState.items.filter(function(item) {
          return item.id !== action.itemId;
        });
        newState.items.push({
          id: action.itemId,
          menuItemId: action.menuItemId,
          quantity: action.quantity
        });
        break;
      case ActionEnum.CHANGE_ORDER_ITEM_QUANTITY:
        newState.items = newState.items.map(function(item) {
          if (action.itemId === item.id) {
            return { id: item.id, menuItemId: item.menuItemId, quantity: action.quantity };
          } else {
            return item;
          }
        });
        break;
      case ActionEnum.CLEAR_ORDER:
        newState = DEFAULT_STATE;
        break;
      case ActionEnum.CLEAR_3DS_AUTHORIZATION:
        newState.pendingPreAuthId = undefined;
        break;
      case ActionEnum.REQUEST_3DS_AUTHORIZATION:
        newState.pendingPreAuthId = action.preAuthId;
        break;
      case ActionEnum.REMOVE_ORDER_ITEM:
        newState.items = newState.items.filter(function(item) {
          return action.itemId !== item.id;
        });
        break;
      case ActionEnum.SET_COMMENTS:
        newState.comments = action.comments;
        break;
      case ActionEnum.SET_DELIVERY_ADDRESS:
        newState.deliveryAddress = action.deliveryAddress;
        break;
      case ActionEnum.SET_ORDER_ID:
        newState.id = action.id;
        break;
      case ActionEnum.SET_PAYMENT_TYPE:
        newState.paymentType = action.paymentType;
        break;
      case ActionEnum.SET_PHONE:
        newState.phone = action.phone;
        break;
      case ActionEnum.SET_PROMO_CODE:
        newState.promoCode = action.promoCode;
        break;
      case ActionEnum.SET_STATUS:
        newState.status = action.status;
      case ActionEnum.SET_WHEN:
        newState.when = action.when;
        break;
      case ActionEnum.SET_WHERE:
        newState.where = action.where;
        break;
      case ActionEnum.SET_ZIP_CODE:
        newState.zipCode = action.zipCode;
        break;
      }

      return newState;
    };
  }

  app.factory('CurrentOrderReducer', [
    'ActionEnum',
    CurrentOrderReducerFactory
  ]);

  return CurrentOrderReducerFactory;
})();
CurrentOrderReducerFactory = undefined;

/**
 * @typedef ItemsState
 * @property {Array<string>} customized
 * @property {Array<string>} specific
 */

/**
 * @typedef {import("redux").Reducer<ItemsState, import("redux").AnyAction>} ItemsReducer
 */
var ItemsReducerFactory = (function() {
  var app = angular.module('priorWeb');

  /**
   * @param {typeof ActionEnum} ActionEnum
   * @returns {ItemsReducer}
   */
  function ItemsReducerFactory(ActionEnum) {
    /**
     * @param {ItemsState} state
     * @param {ItemsReducerAction} action
     */
    return function itemsReducer(state, action) {
      /** @type ItemsState */
      var newState;
      if (typeof state === 'undefined') {
        newState = { customized: [], specific: [] };
      } else {
        newState = angular.copy(state);
      }

      switch (action.type) {
        case ActionEnum.REMOVE_CUSTOMIZED_ITEM:
          newState.customized = newState.customized.filter(function(itemJson) {
            return JSON.parse(itemJson).MenuItem.id !== action.itemId;
          });
          break;
        case ActionEnum.REMOVE_SPECIFIC_ITEM:
          newState.specific = newState.specific.filter(function(itemJson) {
            return JSON.parse(itemJson).MenuItem.id !== action.itemId;
          });
          break;
        case ActionEnum.SAVE_CUSTOMIZED_ITEM:
          newState.customized.push(JSON.stringify(action.item));
          break;
        case ActionEnum.SAVE_SPECIFIC_ITEM:
          newState.specific.push(JSON.stringify(action.item));
          break;
      }

      return newState;
    };
  }

  app.factory('ItemsReducer', [
    'ActionEnum',
    ItemsReducerFactory
  ]);

  return ItemsReducerFactory;
})();
ItemsReducerFactory = undefined;

(function() {
  angular
    .module('uib/template/accordion/accordion-group.html', [])
    .run([
      '$templateCache',
      function($templateCache) {
        $templateCache.put(
          'uib/template/accordion/accordion-group.html',
          '<div class="panel" ng-class="panelClass || \'panel-default\'">\n' +
          '  <div role="tab" id="{{::headingId}}" aria-selected="{{isOpen}}" class="panel-heading" ng-keypress="toggleOpen($event)">\n' +
          '    <h4 class="panel-title">\n' +
          '      <a role="button" data-toggle="collapse" href aria-expanded="{{isOpen}}" aria-controls="{{::panelId}}" tabindex="0" class="accordion-toggle" ng-click="toggleOpen()" uib-accordion-transclude="heading"><span uib-accordion-header ng-class="{\'text-muted\': isDisabled}">{{heading}}</span></a>\n' +
          '    </h4>\n' +
          '  </div>\n' +
          '  <div id="{{::panelId}}" aria-labelledby="{{::headingId}}" aria-hidden="{{!isOpen}}" role="tabpanel" class="panel-collapse collapse" uib-collapse="!isOpen">\n' +
          '    <div class="panel-body" ng-transclude></div>\n' +
          '  </div>\n' +
          '</div>'
        );
      }]);
})();

(function() {
  angular
    .module('uib/template/accordion/accordion.html', [])
    .run([
      '$templateCache',
      function($templateCache) {
        $templateCache.put(
          'uib/template/accordion/accordion.html',
          '<div role="tablist" ng-transclude></div>'
        );
      }
    ]);
})();

var Alert = (function() {

  var app = angular.module('priorWeb');

  /**
   * @param {ng.IRootScopeService} $rootScope
   * @param {ng.ISCEService} $sce
   * @param {ng.ui.bootstrap.IModalService} $uibModal
   */
  function Alert($rootScope, $sce, $uibModal) {
    var scope = $rootScope.$new();

    this.getScope = function getScope() {
      return scope;
    };

    this.getSceService = function getSceService() {
      return $sce;
    };

    this.getUibModal = function getUibModal() {
      return $uibModal;
    };
  }

  /**
   * @param {string} message
   * @param {string} [title="ALERT_MODAL.OK_BUTTON_LABEL"]
   * @param {string} [dismissText="ALERT_MODAL.OK_BUTTON_LABEL"]
   * @param {string} [size="sm"]
   */
  Alert.prototype.show = function showAlert(message, title, dismissText, size) {
    var modalScope = this.getScope().$new();
    size = size || 'sm';

    modalScope['alertDismisText'] = dismissText || 'ALERT_MODAL.OK_BUTTON_LABEL';
    modalScope['alertMessage'] = this.getSceService().trustAsHtml(message);
    modalScope['alertTitle'] = title || 'ALERT_MODAL.ERROR_TITLE';

    return this.getUibModal().open({
      templateUrl: 'templates/alert.html',
      size: size,
      scope: modalScope,
      backdrop: 'static'
    });
  };

  app.factory('Alert', [
    '$rootScope', '$sce', '$uibModal',
    /**
     * @param {ng.IRootScopeService} $rootScope
     * @param {ng.ISCEService} $sce
     * @param {ng.ui.bootstrap.IModalService} $uibModal
     */
    function($rootScope, $sce, $uibModal) {
      return new Alert($rootScope, $sce, $uibModal);
    }
  ]);

  return Alert;
}());
Alert = undefined;

var CordovaFacebook = (function() {
  /**
   * @param {ng.IQService} $q
   * @param {object} ezfb
   */
  function CordovaFacebook($q, ezfb) {
    /** @type {CordovaFacebook} */
    var fb;

    var grantedPermissions = [];

    this.getLoginStatus = function() {
      return ezfb.getLoginStatus();
    };

    this.login = function(permissions) {
      grantedPermissions = [];
      return ezfb.login(null, {
        auth_type: 'rerequest',
        scope: permissions.sort().join(','),
        return_scopes: true
      }).then(function(response) {
        grantedPermissions = response.authResponse.grantedScopes.split(',').sort();

        return response;
      });
    };

    this.checkHasCorrectPermissions = /** @param {string[]} permissions */ function(permissions) {
      /** @type {ng.IDeferred<boolean>} */
      var d = $q.defer(),
          missingPermissions = [];

      for (var i = 0; i < permissions.length; i++) {
        if (grantedPermissions.indexOf(permissions[i]) === -1) {
          missingPermissions.push(permissions[i]);
        }
      }

      if (missingPermissions.length > 0) {
        d.reject(missingPermissions);
      } else {
        d.resolve(true);
      }

      return d.promise;
    };

    this.logout = function() {
      return ezfb.logout();
    };

    this.api = function() {
      return $q.resolve();
    }
  }

  var app = angular.module('priorWeb');
  app.service('$cordovaFacebook', [
    '$q', 'ezfb',
    CordovaFacebook
  ]);

  return CordovaFacebook;
})();
CordovaFacebook = undefined;

(function() {
  var app = angular.module('priorWeb');

  app.factory('$cordovaGeolocation', [
    function() {
      function $cordovaGeolocation() {
      }

      return new $cordovaGeolocation();
    }
  ]);
})();

var ga;
var CordovaGoogleAnalytics = (function() {
  var app = angular.module('priorWeb');

  /**
   * @constructor
   */
  function CordovaGoogleAnalytics() {
    /**
     * @param {string} id
     */
    this.startTrackerWithId = function startTrackerWithId(id) {
      ga('create', id, 'auto');
      ga('require', 'ecommerce');
    };

    /**
     * @param {string} userId
     */
    this.setUserId = function setUserId(userId) {
      ga('set', 'userId', userId);
    };

    this.trackEvent = function trackEvent(category, action, label, value) {
      ga('send', 'event', category, action, label, value);
    };

    this.trackView = function trackView(page, fieldsObject) {
      ga('send', 'pageview', page, fieldsObject);
    };

    this.addTransaction = function addTransaction(id, affiliation, revenue, shipping, tax, currency, autoSend) {
      autoSend = angular.isUndefined(autoSend) ? true : autoSend;
      ga('ecommerce:addTransaction', {
        'id': id,
        'affiliation': affiliation,
        'revenue': revenue,
        'shipping': shipping,
        'tax': tax,
        'currency': currency
      });
      if (autoSend) {
        ga('ecommerce:send');
      }
    };
  }

  app.factory('$cordovaGoogleAnalytics', [
    function() {
      return new CordovaGoogleAnalytics();
    }
  ]);

  return CordovaGoogleAnalytics;
})();
CordovaGoogleAnalytics = undefined;

var WebsiteCurrentOrder = (function() {
  function WebsiteCurrentOrder(
    $q, $window, ActionEnum, BaseRepository, CacheFactory, CurrentOrder, CurrentUser, InvalidOrder, MenuItemType,
    MenuItemsRepository, Order, OrderItemsRepository, OrderLocationEnum, OrderStatusEnum, RestaurantsRepository, State, moment
  ) {
    var items = [],
        itemIds = {};

    CurrentOrder.apply(
      this, [
        $q, $window, BaseRepository, CacheFactory, CurrentUser, InvalidOrder, MenuItemType, MenuItemsRepository,
        Order, OrderLocationEnum, OrderStatusEnum, moment
      ]
    );

    this.restoreFromOrder = function restoreFromOrder(order) {
      this.setComments(order.getComments());
      this.setDeliveryAddress(order.getDeliveryInfo().getAddress());
      this.setId(order.getId());
      this.setPaymentType(order.getPaymentType());
      this.setPhone(order.getDeliveryInfo().getPhone());
      this.setPromoCode(order.getPromoCode());
      this.setRestaurantId(order.getRestaurantId());
      this.setStatus(order.getStatus());
      this.setWhen(order.getWhen());
      this.setWhere(order.getWhere());
      this.setZipCode(order.getDeliveryInfo().getZipCode());
      this.saveAll(OrderItemsRepository.getForOrderId(order.getId()));
    };

    (function(dis) {
      function wrap(fnName, beforeFn, afterFn) {
        var baseFn = dis[fnName];
        dis[fnName] = function() {
          if (beforeFn) {
            beforeFn.apply(dis, arguments);
          }

          var result = baseFn.apply(dis, arguments);

          if (afterFn) {
            result = afterFn.apply(dis, [result].concat(Array.prototype.slice.call(arguments)));
          }

          return result;
        };
      }

      function initAfter() {
        var restaurant = RestaurantsRepository.get(this.getRestaurantId());

        if (!restaurant.hasAsapOrder()) {
          this.setWhen(null);
        }
      }

      function increaseOrderItemQuantityAfter() {
        var menuItemId = arguments[1],
            orderItem = dis.query({ menuItemId: menuItemId })[0],
            newQuantity = orderItem ? orderItem.getQuantity() : 0;

        State.dispatch({
          type: ActionEnum.CHANGE_ORDER_ITEM_QUANTITY,
          itemId: orderItem.getId(),
          quantity: newQuantity
        });

        return arguments[0];
      }

      function decreaseOrderItemQuantityBefore() {
        var menuItemId = arguments[1],
            orderItem = dis.query({ menuItemId: menuItemId })[0],
            quantity = orderItem ? orderItem.getQuantity() : 0;

        if (quantity === 1) {
          State.dispatch({
            type: ActionEnum.REMOVE_ORDER_ITEM,
            itemId: orderItem.getId()
          });
        }
      }

      function decreaseOrderItemQuantityAfter() {
        var menuItemId = arguments[1],
            orderItem = dis.query({ menuItemId: menuItemId })[0],
            newQuantity = orderItem ? orderItem.getQuantity() : 0;

        if (newQuantity > 0) {
          State.dispatch({
            type: ActionEnum.CHANGE_ORDER_ITEM_QUANTITY,
            itemId: orderItem.getId(),
            quantity: newQuantity
          });
        }

        return arguments[0];
      }

      wrap('init', undefined, initAfter);

      wrap('setComments', undefined, function setCommentsAfter() {
        State.dispatch({
          type: ActionEnum.SET_COMMENTS,
          comments: arguments[1]
        });

        return arguments[0];
      });

      wrap('setDeliveryAddress', undefined, function setDeliveryAddressAfter() {
        State.dispatch({
          type: ActionEnum.SET_DELIVERY_ADDRESS,
          deliveryAddress: arguments[1]
        });

        return arguments[0];
      });

      wrap('setId', undefined, function setIdAfter() {
        State.dispatch({
          type: ActionEnum.SET_ORDER_ID,
          id: arguments[1]
        });

        return arguments[0];
      });

      wrap('setPaymentType', undefined, function setPaymentType() {
        State.dispatch({
          type: ActionEnum.SET_PAYMENT_TYPE,
          paymentType: arguments[1]
        });

        return arguments[0];
      });

      wrap('setPhone', undefined, function setPhoneAfter() {
        State.dispatch({
          type: ActionEnum.SET_PHONE,
          phone: arguments[1]
        });

        return arguments[0];
      });

      wrap('setPromoCode', undefined, function setPromoCodeAfter() {
        State.dispatch({
          type: ActionEnum.SET_PROMO_CODE,
          promoCode: arguments[1]
        });

        return arguments[0];
      });

      wrap('setStatus', undefined, function setStatusAfter() {
        State.dispatch({
          type: ActionEnum.SET_STATUS,
          status: arguments[1]
        });

        return arguments[0];
      });

      wrap('setWhen', undefined, function setWhenAfter() {
        State.dispatch({
          type: ActionEnum.SET_WHEN,
          when: arguments[1]
        });

        return arguments[0];
      });

      wrap('setWhere', undefined, function setWhereAfter() {
        State.dispatch({
          type: ActionEnum.SET_WHERE,
          where: arguments[1]
        });

        return arguments[0];
      });

      wrap('setZipCode', undefined, function setZipCodeAfter() {
        State.dispatch({
          type: ActionEnum.SET_ZIP_CODE,
          zipCode: arguments[1]
        });

        return arguments[0];
      });

      wrap('saveOrder', undefined, function saveOrderAfter() {
        return arguments[0];
      });

      wrap('save', undefined, function saveAfter() {
        var orderItem = arguments[1];
        State.dispatch({
          type: ActionEnum.ADD_ORDER_ITEM,
          itemId: orderItem.getId(),
          menuItemId: orderItem.getMenuItemId(),
          quantity: orderItem.getQuantity()
        });

        return arguments[0];
      });

      wrap('clear', undefined, function clearAfter() {
        State.dispatch({ type: ActionEnum.CLEAR_ORDER });

        return arguments[0];
      });

      wrap('increaseItemQuantity', undefined, increaseOrderItemQuantityAfter);
      wrap('decreaseItemQuantity', decreaseOrderItemQuantityBefore, decreaseOrderItemQuantityAfter);

      wrap('remove', function removeBefore() {
        var itemId = arguments[0],
            orderItem = dis.get(itemId);
        State.dispatch({
          type: ActionEnum.REMOVE_ORDER_ITEM,
          itemId: itemId,
          menuItemId: orderItem.getMenuItemId()
        });

        return arguments[0];
      });
    })(this);
  }

  var app = angular.module('priorCore');
  app.factory('WebsiteCurrentOrderClass', [
    'CurrentOrderClass',
    function(CurrentOrder) {
      WebsiteCurrentOrder.prototype = Object.create(CurrentOrder.prototype, {
        constructor: {
          value: WebsiteCurrentOrder,
          enumerable: false
        }
      });

      return WebsiteCurrentOrder;
    }
  ]);

  app.service('CurrentOrder', [
    '$q', '$window', 'ActionEnum', 'BaseRepositoryClass', 'CacheFactory', 'CurrentOrderClass', 'CurrentUser', 'InvalidOrder',
    'MenuItemType', 'MenuItemsRepository', 'Order', 'OrderItemsRepository', 'OrderLocationEnum', 'OrderStatusEnum',
    'RestaurantsRepository', 'State', 'WebsiteCurrentOrderClass', 'moment',
    /**
     * @param {ng.IQService} $q
     * @param {ng.IWindowService} $window
     * @param {typeof BaseRepository} BaseRepository
     * @param {CacheFactory} CacheFactory
     * @param {CurrentUser} CurrentUser
     * @param {typeof InvalidOrder} InvalidOrder
     * @param {typeof MenuItemType} MenuItemType
     * @param {typeof OrderLocationEnum} OrderLocationEnum
     * @param {typeof OrderStatusEnum} OrderStatusEnum
     * @param {any} moment
     */
    function(
      $q, $window, ActionEnum, BaseRepository, CacheFactory, CurrentOrder, CurrentUser, InvalidOrder, MenuItemType,
      MenuItemsRepository, Order, OrderItemsRepository, OrderLocationEnum, OrderStatusEnum, RestaurantsRepository,
      State, WebsiteCurrentOrder, moment
    ) {
      return new WebsiteCurrentOrder(
        $q, $window, ActionEnum, BaseRepository, CacheFactory, CurrentOrder, CurrentUser, InvalidOrder, MenuItemType,
        MenuItemsRepository, Order, OrderItemsRepository, OrderLocationEnum, OrderStatusEnum, RestaurantsRepository,
        State, moment
      );
    }
  ]);

  return WebsiteCurrentOrder;
})();
WebsiteCurrentOrder = undefined;

/* global fbq */
(function() {
  var app = angular.module('priorWeb');

  app.factory('FbPixel', ['FbPixelEventEnum',
    function(FbPixelEventEnum) {
      function FbPixel() {
        var ready = false;

        this.init = function init(pixelId) {
          !function(f,b,e,v,n,t,s){
            if(f.fbq)
              return;
            n=f.fbq=function(){
              n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments);
            };
            if(!f._fbq)f._fbq=n;
            n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);
          }(
            window,
            document,
            'script','https://connect.facebook.net/en_US/fbevents.js'
          );
          fbq('init', pixelId);
          fbq('track', FbPixelEventEnum.PAGE_VIEW);
          ready = true;
        };

        this.track = function track(event, params) {
          if (!ready) {
            throw 'Facebook Pixel not initialized';
          }
          fbq('track', event, params);
        };
      }

      return new FbPixel();
    }
  ]);
})();

/**
 * @typedef IGoogleAnalyticsProvider
 * @prop {(useGtag: boolean) => void} setUseGtag
 */

/**
 * @typedef {IGoogleAnalyticsProvider & ng.IServiceProvider} GoogleAnalyticsProvider
 */

var GoogleAnalyticsWrapper = (function(angular) {
  function GoogleAnalyticsProvider() {
    var useGtag = true;

    this.setUseGtag = /** @param {boolean} newValue */ function setUseGtag(newValue) {
      useGtag = newValue;
    }

    this.$get = [
      'GoogleGlobalTag', 'GoogleTagManager',
      /**
       * @param {object} googleGlobalTag
       * @param {GoogleTagManager} googleTagManager
       */
      function GoogleAnalyticsFactory(googleGlobalTag, googleTagManager) {
        return new GoogleAnalyticsWrapper(useGtag, googleGlobalTag, googleTagManager);
      }
    ];
  }

  /**
   * @param {boolean} useGtag
   * @param {GoogleGlobalTag} googleGlobalTag
   * @param {GoogleTagManager} googleTagManager
   */
  function GoogleAnalyticsWrapper(useGtag, googleGlobalTag, googleTagManager) {
    this.useGtag = function() {
      return useGtag;
    }

    this.getGtag = function() {
      return googleGlobalTag;
    }

    this.getGtm = function() {
      return googleTagManager;
    }
  }

  GoogleAnalyticsWrapper.prototype.setUserId =
    /**
     * @param {string} newUserId
     */
    function(newUserId) {
      this.getGtag().setUserId(newUserId);
      this.getGtm().setUserId(newUserId);
    };

  GoogleAnalyticsWrapper.prototype.trackEvent =
    /**
     * @param {string} propertyId
     * @param {string} category
     * @param {string} action
     * @param {string} [label='']
     * @param {number} [value=0]
     */
    function gaTrackEvent(propertyId, category, action, label, value) {
      this.useGtag() ?
        this.getGtag().trackEvent(action, {
          event_category: category,
          event_label: label,
          send_to: propertyId,
          value: value
        }) :
        this.getGtm().trackGAEvent(propertyId, category, action, label, value);
    }

  GoogleAnalyticsWrapper.prototype.trackTransaction =
    /**
     * @param {string} propertyId
     * @param {string} transactionId
     * @param {string} affiliation
     * @param {number} value
     * @param {string} shipping
     * @param {string} tax
     * @param {string} currency
     */
    function gaTrackTransaction(propertyId, transactionId, affiliation, value, currency, tax, shipping) {
      this.useGtag() ?
        this.getGtag().trackTransaction(propertyId, transactionId, affiliation, value, currency, shipping, tax) :
        this.getGtm().trackGATransaction(propertyId, transactionId, affiliation, value, currency, shipping, tax);
    };

  GoogleAnalyticsWrapper.prototype.trackView =
    /**
     * @param {string} propertyId
     * @param {string} pageName
     */
    function gaTrackView(propertyId, pageName) {
      this.useGtag() ? this.getGtag().trackView(propertyId, pageName) : this.getGtm().trackGAView(propertyId, pageName);
    };

  var app = angular.module('priorWeb');
  app.provider('GoogleAnalytics', GoogleAnalyticsProvider);

  return GoogleAnalyticsWrapper;
})(angular);
GoogleAnalyticsWrapper = undefined;

var GoogleGlobalTag = (
  /**
   * @param {angular} angular
   * @param {array} dataLayer
   */
  function(angular, dataLayer) {
    /**
     * @param {ng.IDocumentService} $document
     * @param {string[]} gtagPropertyIds
     */
    function GoogleGlobalTag($document, gtagPropertyIds) {
      var firstTag = $document[0].getElementsByTagName('script')[0],
          newTag = $document[0].createElement('script');
      newTag.async = true;
      newTag.src = 'https://www.googletagmanager.com/gtag/js?id=' + gtagPropertyIds[0];
      firstTag.parentNode.insertBefore(newTag, firstTag);

      function gtag() {
        dataLayer.push(arguments);
      }

      /** @type {Gtag.Gtag} */
      this.gtag = gtag;

      gtag('js', new Date());
      gtagPropertyIds.forEach(function (propertyId) {
        gtag('config', propertyId, {
          'send_page_view': false
        });
      });
    }

    GoogleGlobalTag.prototype.setUserId = /** @param {string} userId */ function gtagSetUserId(userId) {
      this.gtag('set', { 'user_id': userId });
    };

    GoogleGlobalTag.prototype.trackConversion =
      /**
       * @param {string} propertyId
       * @param {string} transactionId
       */
      function gtagTrackConversion(propertyId, transactionId) {
        this.trackEvent('conversion', {
          'send_to': propertyId,
          'transaction_id': transactionId
        });
      };

    GoogleGlobalTag.prototype.trackEvent =
      /**
       * @param {Gtag.EventNames | string} eventName
       * @param {Gtag.ControlParams | Gtag.EventParams | Gtag.CustomParams} eventParams
       */
      function gtagTrackEvent(eventName, eventParams) {
        this.gtag('event', eventName, eventParams);
      }

    GoogleGlobalTag.prototype.trackTransaction =
      /**
       * @param {string} propertyId
       * @param {string} transactionId
       * @param {string} affiliationId
       * @param {number} value
       * @param {string} currency
       * @param {string} [shipping=0]
       * @param {string} [tax=0]
       * @param {Gtag.Item[]} [items]
       * @param {string} [coupon]
       */
      function gtagTrackTransaction(
        propertyId,
        transactionId,
        affiliationId,
        value,
        currency,
        shipping,
        tax,
        items,
        coupon
      ) {
        this.trackEvent('purchase', {
          'send_to': propertyId,
          'transaction_id': transactionId,
          'affiliation': affiliationId,
          'value': value,
          'currency': currency,
          'tax': tax || 0,
          'shipping': shipping || 0,
          'items': items,
          'coupon': coupon
        });
      };

    GoogleGlobalTag.prototype.trackView =
      /**
       * @param {string} propertyId
       * @param {string} pageName
       */
      function gtagTrackView(propertyId, pageName) {
        this.gtag('config', propertyId, {
          'page_path': '/' + pageName
        });
      };

    var app = angular.module('priorWeb');
    app.factory('GoogleGlobalTag', [
      '$document', 'gtagPropertyIds',
      /**
       * @param {ng.IDocumentService} $document
       * @param {string[]} gtagPropertyIds
       */
      function($document, gtagPropertyIds) {
        return new GoogleGlobalTag($document, gtagPropertyIds);
      }
    ]);

    return GoogleGlobalTag;
  }
)(angular, dataLayer);
GoogleGlobalTag = undefined;

var GooglePlacesAutocomplete = (function(angular, google) {
  function GooglePlacesAutocomplete() {
    if (!google.maps.places.Autocomplete) {
      throw new Error('Could not find Google Places Autocomplete library');
    }
  }

  GooglePlacesAutocomplete.prototype.autocomplete = function GPAAutocomplete(input, options, callback, fields) {
    var autocomplete = new google.maps.places.Autocomplete(input, options);

    if (fields) {
      autocomplete.setFields(fields);
    }

    autocomplete.addListener('place_changed', callback);

    return autocomplete;
  };

  var app = angular.module('priorWeb');
  app.service('GooglePlacesAutocomplete', [
    function() {
      return new GooglePlacesAutocomplete()
    }
  ]);

  return GooglePlacesAutocomplete;
})(angular, google);
GooglePlacesAutocomplete = undefined;

var dataLayer;
var GoogleTagManager = (
  /**
   * @param {angular} angular
   * @param {array} dataLayer
   */
  function(angular, dataLayer) {
    /**
     * @param {ng.IDocumentService} $document
     * @param {string[]} gtmContainerIds
     */
    function GoogleTagManager($document, gtmContainerIds) {
      /** @type {string} */
      var userId;

      gtmContainerIds.forEach(/** @param {string} containerId */ function initContainer(containerId) {
        dataLayer.push({
          'gtm.start': new Date().getTime(),
          event:'gtm.js'
        });

        var firstTag = $document[0].getElementsByTagName('script')[0],
            newTag = $document[0].createElement('script');
        newTag.async = true;
        newTag.src = 'https://www.googletagmanager.com/gtm.js?id=' + containerId;
        firstTag.parentNode.insertBefore(newTag, firstTag);
      });

      this.getUserId = function gtmgetUserId() {
        return userId;
      };

      this.setUserId = /** @param {string} newUserId */ function gtmsetUserId(newUserId) {
        userId = newUserId;
      };
    }

    GoogleTagManager.prototype.trackGAEvent =
      /**
       * @param {string} propertyId
       * @param {string} category
       * @param {string} action
       * @param {string} [label='']
       * @param {number} [value=0]
       */
      function gtmTrackEvent(propertyId, category, action, label, value) {
        this.triggerMessage({
          'event': 'PriorEvent',
          'trackerId': propertyId,
          'event_category': category,
          'event_action': action,
          'event_label': label,
          'event_value': value
        });
      };

    GoogleTagManager.prototype.trackGATransaction =
      /**
       * @param {string} propertyId
       * @param {string} transactionId
       * @param {string} affiliation
       * @param {number} total
       * @param {string} currency
       * @param {string} shipping
       * @param {string} tax
       */
      function gtmTrackTransaction(propertyId, transactionId, affiliation, total, currency, shipping, tax) {
        this.triggerMessage({
          'event': 'PriorTransaction',
          'trackerId': propertyId,
          'transactionId': transactionId,
          'transactionAffiliation': affiliation,
          'transactionCurrency': currency,
          'transactionTotal': total,
          'transactionShipping': shipping,
          'transactionTax': tax
        });
      };

    GoogleTagManager.prototype.trackGAView =
      /**
       * @param {string} propertyId
       * @param {string} page
       */
      function gtmTrackGAView(propertyId, page) {
        this.triggerMessage({
          'attributes': {
            'page': page
          },
          'event': 'PriorPageView',
          'trackerId': propertyId
        });
      };

    /**
     * @param {object} data
     */
    GoogleTagManager.prototype.triggerMessage = function(data) {
      data.userId = this.getUserId();

      dataLayer.push(data);
    };

    var app = angular.module('priorWeb');
    app.factory('GoogleTagManager', [
      '$document', 'gtmContainerIds',
      /**
       * @param {ng.IDocumentService} $document
       * @param {string[]} gtmContainerIds
       */
      function GoogleTagManagerFactory($document, gtmContainerIds) {
        return new GoogleTagManager($document, gtmContainerIds);
      }
    ]);

    return GoogleTagManager;
  }
)(angular, dataLayer);
GoogleTagManager = undefined;

var LoginModal = (function() {
  var app = angular.module('priorWeb');

  /**
   * @param {ng.ui.bootstrap.IModalService} $uibModal
   */
  function LoginModal($uibModal) {
    this.getUibModal = function getUibModal() {
      return $uibModal;
    };
  }

  LoginModal.prototype.open = function open() {
    return this.getUibModal().open({
      animation: true,
      templateUrl: 'templates/login-enter-modal.html',
      controller: 'LoginController'
    }).result.catch(function(err) {
      console.log(err);
    });
  };

  app.factory('loginModal', [
    '$uibModal',
    /**
     * @param {ng.ui.bootstrap.IModalService} $uibModal
     */
    function($uibModal) {
      return new LoginModal($uibModal);
    }
  ]);

  return LoginModal;
}());
LoginModal = undefined;

/** @type {import("redux")} */
var Redux;
var State = (function() {
  var app = angular.module('priorWeb');

  /**
   * @param {import("ngstorage").ngStorage.StorageService} $localStorage
   * @param {ng.IWindowService} $window
   * @param {AppReducer} appReducer
   * @param {CurrentOrderReducer} currentOrderReducer
   * @param {ItemsReducer} itemsReducer
   */
  function State($localStorage, $window, appReducer, currentOrderReducer, itemsReducer) {
    var initialState = $localStorage.state ? $localStorage.state : undefined,
        reducers = {
          app: appReducer,
          currentOrder: currentOrderReducer,
          items: itemsReducer
        },
        store = Redux.createStore(
          Redux.combineReducers(reducers),
          initialState,
          $window.__REDUX_DEVTOOLS_EXTENSION__ && $window.__REDUX_DEVTOOLS_EXTENSION__()
        );

    /** @returns {import("redux").Store} */
    this.getStore = function getStore() {
      return store;
    };
  }

  /** @param {import("redux").AnyAction} action */
  State.prototype.dispatch = function dispatch(action) {
    this.getStore().dispatch(action);
  };

  State.prototype.get = function get() {
    return this.getStore().getState();
  };

  /** @param {() => void} listener */
  State.prototype.subscribe = function subscribe(listener) {
    this.getStore().subscribe(listener);
  };

  app.factory('State', [
    '$localStorage', '$window', 'AppReducer', 'CurrentOrderReducer', 'ItemsReducer',
    /**
     * @param {import("ngstorage").ngStorage.StorageService} $localStorage
     * @param {ng.IWindowService} $window
     * @param {AppReducer} appReducer
     * @param {CurrentOrderReducer} currentOrderReducer
     * @param {ItemsReducer} itemsReducer
     */
    function StateFactory($localStorage, $window, appReducer, currentOrderReducer, itemsReducer) {
      return new State($localStorage, $window, appReducer, currentOrderReducer, itemsReducer);
    }
  ]);

  return State;
})();
State = undefined;

(function() {
  var app = angular.module('priorWeb');

  app.factory('CategoryViewModel', [
    function() {
      function CategoryViewModel(category) {
        var name = category.getName();
        this.open = true;

        this.getName = function() {
          return name;
        };

        this.isOpen = function() {
          return this.open;
        };

        this.setIsOpen = function(newValue) {
          this.open = newValue;
        };
      }

      CategoryViewModel.build = function(category) {
        return new CategoryViewModel(category);
      };

      return CategoryViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('CompositeMenuItemViewModel', [
    'MenuItemViewModel',
    function(MenuItemViewModel) {
      function CompositeMenuItemViewModel(menuItem) {
        MenuItemViewModel.apply(this, [menuItem]);

        this.getView = function() {
          return 'templates/menu-item.html';
        };
      }

      CompositeMenuItemViewModel.prototype = Object.create(MenuItemViewModel.prototype, {
        constructor: {
          value: CompositeMenuItemViewModel,
          enumerable: false
        }
      });

      CompositeMenuItemViewModel.build = function(menuItem) {
        return new CompositeMenuItemViewModel(menuItem);
      };

      return CompositeMenuItemViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('CustomizedCompositeMenuItemViewModel', [
    'CompositeMenuItemViewModel',
    function(CompositeMenuItemViewModel) {
      function CustomizedCompositeMenuItemViewModel(menuItem) {
        CompositeMenuItemViewModel.apply(this, [menuItem]);
      }

      CustomizedCompositeMenuItemViewModel.prototype = Object.create(CompositeMenuItemViewModel.prototype, {
        constructor: {
          value: CustomizedCompositeMenuItemViewModel,
          enumerable: false
        }
      });

      CustomizedCompositeMenuItemViewModel.build = function(menuItem) {
        return new CustomizedCompositeMenuItemViewModel(menuItem);
      };

      return CustomizedCompositeMenuItemViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('EditableCompositeMenuItemViewModel', [
    'CompositeMenuItemViewModel',
    function(CompositeMenuItemViewModel) {
      function EditableCompositeMenuItemViewModel(menuItem) {
        CompositeMenuItemViewModel.apply(this, [menuItem]);

        this.getView = function() {
          return 'templates/editable-composite-menu-item.html';
        };
      }

      EditableCompositeMenuItemViewModel.prototype = Object.create(CompositeMenuItemViewModel.prototype, {
        constructor: {
          value: EditableCompositeMenuItemViewModel,
          enumerable: false
        }
      });

      EditableCompositeMenuItemViewModel.build = function(menuItem) {
        return new EditableCompositeMenuItemViewModel(menuItem);
      };

      return EditableCompositeMenuItemViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('GenericMenuItemViewModel', [
    'MenuItemViewModel',
    function(MenuItemViewModel) {
      function GenericMenuItemViewModel(menuItem) {
        MenuItemViewModel.apply(this, [menuItem]);

        this.getView = function() {
          return 'templates/generic-menu-item.html';
        };
      }

      GenericMenuItemViewModel.prototype = Object.create(MenuItemViewModel.prototype, {
        constructor: {
          value: GenericMenuItemViewModel,
          enumerable: false
        }
      });

      GenericMenuItemViewModel.build = function(menuItem) {
        return new GenericMenuItemViewModel(menuItem);
      };

      return GenericMenuItemViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('MenuItemViewModel', [
    'NotImplemented', 'apiEndpoint',
    function(NotImplemented, apiEndpoint) {
      function MenuItemViewModel(menuItem) {
        var id = menuItem.getId(),
            image = menuItem.getImage(),
            name = menuItem.getName(),
            description = menuItem.getDescription(),
            price = menuItem.getPrice(),
            selected = false,
            quantity = 0;

        function isSelected() {
          return selected;
        }
        this.isSelected = isSelected;

        function setIsSelected(newValue) {
          selected = newValue;
        }
        this.setIsSelected = setIsSelected;

        this.getId = function() {
          return id;
        };

        this.getImageName = function getImageName() {
          var img = image ? image.getName() : name;

          return img;
        };

        this.getImagePath = function getImagePath() {
          var img = image ? image.getPath() : undefined;

          return img;
        };

        this.getName = function() {
          return name;
        };

        this.getDescription = function() {
          return description;
        };

        this.getPrice = function() {
          return price;
        };

        this.getTotal = function() {
          return quantity * price;
        };

        this.getQuantity = function() {
          return quantity;
        };

        this.setQuantity = function(newQuantity) {
          quantity = newQuantity;
        };

        this.getView = function() {
          throw NotImplemented.build({message: 'Need to implement getView'});
        };
      }

      MenuItemViewModel.build = function(menuItem) {
        return new MenuItemViewModel(menuItem);
      };

      return MenuItemViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('NonStandaloneMenuItemViewModel', [
    'MenuItemViewModel',
    function(MenuItemViewModel) {
      function NonStandaloneMenuItemViewModel(menuItem) {
        MenuItemViewModel.apply(this, [menuItem]);

        this.getView = function() {
          return 'templates/non-standalone-menu-item.html';
        };
      }

      NonStandaloneMenuItemViewModel.prototype = Object.create(MenuItemViewModel.prototype, {
        constructor: {
          value: NonStandaloneMenuItemViewModel,
          enumerable: false
        }
      });

      NonStandaloneMenuItemViewModel.build = function(menuItem) {
        return new NonStandaloneMenuItemViewModel(menuItem);
      };

      return NonStandaloneMenuItemViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('OrderReviewViewModel', [
    '$translate', 'CurrentOrder', 'CurrentUser', 'OrderLocationEnum', 'PaymentProviderEnum', 'RestaurantsRepository', 'moment',
    function($translate, CurrentOrder, CurrentUser, OrderLocationEnum, PaymentProviderEnum, RestaurantsRepository, moment) {
      function OrderReviewViewModel() {
        var comments = CurrentOrder.getComments() ? CurrentOrder.getComments() : '',
            name = CurrentUser.get().getName(),
            phone = CurrentOrder.getPhone(),
            restaurant = RestaurantsRepository.get(CurrentOrder.getRestaurantId()),
            total = CurrentOrder.getTotal(),
            when = CurrentOrder.getWhen(),
            where = CurrentOrder.getWhere(),
            COMMENTS_REGEX = /^([^\t\n]*)(\t\n)?(.*)$/s;

        this.getCardNumber = function() {
          return CurrentUser.get().getCard().getNumber();
        }

        this.getComments = function() {
          return comments.indexOf('\t\n') > -1 ? comments.replace(COMMENTS_REGEX, '$3') : comments;
        }

        this.getName = function() {
          return name;
        }

        this.getPhone = function() {
          return phone ? phone : $translate.instant('ORDER_REVIEW_MODAL.PHONE_NOT_GIVEN');
        }

        this.getRequestedDate = function() {
          return when == -1 ? '' : moment(when).format('dddd, D MMM YYYY');
        };

        this.getRequestedTime = function() {
          return when == -1 ? '' : moment(when).format('HH:mm');
        };

        this.getRestaurantAddress = function() {
          var restaurantAddress = [restaurant.getAddress()],
              addressComment = restaurant.getAddressComment();

          if (addressComment) {
            restaurantAddress.push(addressComment);
          }

          return restaurantAddress.join(', ');
        }

        this.getTotalText = function() {
          return $translate.instant('ORDER_REVIEW_MODAL.ORDER_TOTAL', {
            total: total
          });
        };

        this.getWhen = function() {
          return when == -1 ? 'asap' : when;
        }

        this.getWhenText = function() {
          return $translate.instant('ORDER_STATUSES_TEMPLATE.WHEN_MESSAGE', {
            when: this.getWhen(),
            date: this.getRequestedDate(),
            time: this.getRequestedTime()
          }, "messageformat");
        };

        this.getWhereText = function() {
          var whereText;
          switch (where) {
            case OrderLocationEnum.TAKEOUT:
              whereText = $translate.instant('ORDER_STATUSES_TEMPLATE.TAKEOUT_LABEL');
              break;
            case OrderLocationEnum.DINING_IN:
              whereText = $translate.instant('ORDER_STATUSES_TEMPLATE.DINING_IN_LABEL');
              break;
            case OrderLocationEnum.CORPORATE_DELIVERY:
              whereText = $translate.instant('ORDER_STATUSES_TEMPLATE.CORPORATE_DELIVERY_LABEL');
              break;
            case OrderLocationEnum.DELIVERY:
              whereText = $translate.instant('ORDER_STATUSES_TEMPLATE.DELIVERY_LABEL', {
                hasDeliveryInfo: true,
                deliveryInfo: CurrentOrder.getDeliveryAddress() + (
                  comments.indexOf('\t\n') > -1 ? ', ' + comments.replace(COMMENTS_REGEX, '$1') : ''
                )
              }, "messageformat");
              break;
          }

          return whereText;
        };

        this.getWhereWhenText= function() {
          var text = [this.getWhereText()];
          if (where !== OrderLocationEnum.DELIVERY && where != OrderLocationEnum.CORPORATE_DELIVERY) {
            text.push(this.getRestaurantAddress());
          }
          text.push(this.getWhenText());

          return text.join(', ');
        };

        this.isCashPaymentOrder = function() {
          return CurrentOrder.getPaymentType() == PaymentProviderEnum.CASH;
        };

        this.isKushkiOrder = function() {
          return CurrentOrder.getPaymentType() == PaymentProviderEnum.KUSHKI;
        };

        this.isMangoPayOrder = function() {
          return CurrentOrder.getPaymentType() == PaymentProviderEnum.MANGOPAY;
        };

        this.isNonRedirectPaymentOrder = function() {
          return this.isKushkiOrder() || this.isMangoPayOrder();
        };

        this.isRappiPayPaymentOrder = function() {
          return CurrentOrder.getPaymentType() == PaymentProviderEnum.RAPPIPAY;
        };

        this.isRedirectPaymentOrder = function() {
          return this.isRedsysPaymentOrder();
        };

        this.isRedsysPaymentOrder = function() {
          return CurrentOrder.getPaymentType() == PaymentProviderEnum.REDSYS;
        };

        this.isTpvPaymentOrder = function() {
          return CurrentOrder.getPaymentType() == PaymentProviderEnum.TPV;
        };
      }

      OrderReviewViewModel.build = function() {
        return new OrderReviewViewModel();
      };

      return OrderReviewViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('OrderViewModel', [
    'OrderLocationEnum', 'RestaurantsRepository', 'moment',
    function(OrderLocationEnum, RestaurantsRepository, moment) {
      function OrderViewModel(order) {
        var currency = order.getCurrency(),
            id = order.getId(),
            restaurantId = order.getRestaurantId(),
            restaurantName = RestaurantsRepository.get(restaurantId).getName(),
            when = order.getWhen(),
            where = parseInt(order.getWhere()),
            total = order.getTotal();

        this.getCurrency = function() {
          return currency;
        };

        this.getId = function() {
          return id;
        };

        this.getTotal = function() {
          return total;
        };

        this.getRequestedDate = function() {
          return when == -1 ? '' : moment(when).format('dddd, D MMM YYYY');
        };

        this.getRequestedTime = function() {
          return when == -1 ? '' : moment(when).format('HH:mm');
        };

        this.getRestaurantId = function() {
          return restaurantId;
        };

        this.getRestaurantName = function() {
          return restaurantName;
        };

        this.getWhen = function() {
          return when == -1 ? 'asap' : when;
        };

        this.getWhere = function() {
          var whereText;
          switch (where) {
            case OrderLocationEnum.TAKEOUT:
              whereText = 'ORDER_STATUSES_TEMPLATE.TAKEOUT_LABEL';
              break;
            case OrderLocationEnum.DINING_IN:
              whereText = 'ORDER_STATUSES_TEMPLATE.DINING_IN_LABEL';
              break;
            case OrderLocationEnum.DELIVERY:
              whereText = 'ORDER_STATUSES_TEMPLATE.DELIVERY_LABEL';
              break;
          }

          return whereText;
        };

        this.getDeliveryInfo = function() {
          var deliveryInfo = order.getDeliveryInfo() ? order.getDeliveryInfo().getFullAddress() : '';
          return {
            hasDeliveryInfo: !!deliveryInfo,
            deliveryInfo: deliveryInfo
          };
        };
      }

      OrderViewModel.build = function(order) {
        return new OrderViewModel(order);
      };

      return OrderViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('RestaurantViewModel', [
    'apiEndpoint',
    function(apiEndpoint) {
      function RestaurantViewModel(restaurant) {
        var id = restaurant.getId(),
            name = restaurant.getName(),
            description = restaurant.getDescription(),
            deliveryCharge = restaurant.getDeliveryCharge(),
            address = restaurant.getAddress(),
            logo = restaurant.getLogo();

        this.getId = function() {
          return id;
        };

        this.getName = function() {
          return name;
        };

        this.getLogo = function() {
          return logo ? apiEndpoint + '/' + logo : undefined;
        };

        this.getDescription = function() {
          return description;
        };

        this.getExtendedDescription = function() {
          return description;
        };

        this.getAddress = function() {
          return address;
        };

        this.getDeliveryCharge = function() {
          return deliveryCharge;
        };

        this.getDistance = function() {
          var distance = restaurant.getDistance();
          if (Number.isFinite(distance)) {
            return ' a ' + (distance < 10000 ? roundDistance(distance) : 'más de 10km') +
              (restaurant.getAddress() ? ', en ' : '');
          } else {
            return '';
          }
        };

        this.getOpeningHours = function() {
          var schedules = restaurant.getSchedules().filter(function(schedule) {
            return schedule.getNumericDay() === ((new Date()).getDay() + 6) % 7;
          });
          if (schedules.length === 1) {
            return schedules[0].getOpeningHours();
          } else {
            return null;
          }
        };

        this.getOrderingAllowedHours = function() {
          var schedules = restaurant.getSchedules().filter(function(schedule) {
            return schedule.getNumericDay() === ((new Date()).getDay() + 6) % 7;
          });

          if (schedules.length === 1) {
            return schedules[0].getOrderingAllowed();
          } else {
            return null;
          }
        }

        function roundDistance(distance) {
          distance = Math.ceil(distance / 100) * 100;
          return (
            distance < 1000 ?
              distance + 'm' :
              Math.round(distance * 100) / 100000 + 'km'
          );
        }
      }

      RestaurantViewModel.build = function(restaurant) {
        return new RestaurantViewModel(restaurant);
      };

      return RestaurantViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('SpecificMenuItemViewModel', [
    'MenuItemViewModel',
    function(MenuItemViewModel) {
      function SpecificMenuItemViewModel(menuItem) {
        MenuItemViewModel.apply(this, [menuItem]);

        this.getView = function() {
          return 'templates/menu-item.html';
        };
      }

      SpecificMenuItemViewModel.prototype = Object.create(MenuItemViewModel.prototype, {
        constructor: {
          value: SpecificMenuItemViewModel,
          enumerable: false
        }
      });

      SpecificMenuItemViewModel.build = function(menuItem) {
        return new SpecificMenuItemViewModel(menuItem);
      };

      return SpecificMenuItemViewModel;
    }
  ]);
})();

(function() {
  var app = angular.module('priorWeb');

  app.factory('StandaloneMenuItemViewModel', [
    'MenuItemViewModel',
    function(MenuItemViewModel) {
      function StandaloneMenuItemViewModel(menuItem) {
        MenuItemViewModel.apply(this, [menuItem]);

        this.getView = function() {
          return 'templates/menu-item.html';
        };
      }

      StandaloneMenuItemViewModel.prototype = Object.create(MenuItemViewModel.prototype, {
        constructor: {
          value: StandaloneMenuItemViewModel,
          enumerable: false
        }
      });

      StandaloneMenuItemViewModel.build = function(menuItem) {
        return new StandaloneMenuItemViewModel(menuItem);
      };

      return StandaloneMenuItemViewModel;
    }
  ]);
})();

//# sourceMappingURL=prior.js.map
